/**
 * The GRIND wire format, mirroring grind/internal/protocol/messages.go.
 *
 * These two files are one contract. Changing a message here without changing it
 * there (or the reverse) silently breaks the game — see `doc/grind/architecture/NETWORKING-PROTOCOL.md`.
 */

// ── Message types ───────────────────────────────────────────────────────────

export const CMD_MOVE_TO = 'CMD_MOVE_TO';
export const CMD_ATTACK_TARGET = 'CMD_ATTACK_TARGET';
export const CMD_RESPAWN = 'CMD_RESPAWN';
export const CMD_PICKUP = 'CMD_PICKUP';
/** Breaks a barrel or urn, by fixture id, in reach and in sight. `design/LEVEL-FIXTURES.md`. */
export const CMD_BREAK = 'CMD_BREAK';
export const CMD_LOCK_ITEM = 'CMD_LOCK_ITEM';
/**
 * Put a carried item in a named cell of a named container.
 *
 * **One command for four gestures** — rearrange the bag, rearrange the vault, deposit,
 * withdraw — because all four are "this item, that container, that cell". The client
 * names a cell and never an ordering: occupancy is the server's model, re-derived on
 * every request, so a client cannot state that two items share a square.
 */
export const CMD_MOVE_ITEM = 'CMD_MOVE_ITEM';
export const CMD_ENTER_ZONE = 'CMD_ENTER_ZONE';
export const CMD_RETURN_TO_HUB = 'CMD_RETURN_TO_HUB';
export const CMD_EQUIP_ITEM = 'CMD_EQUIP_ITEM';
export const CMD_UNEQUIP_ITEM = 'CMD_UNEQUIP_ITEM';
export const CMD_ALLOCATE_ATTRIBUTE = 'CMD_ALLOCATE_ATTRIBUTE';
export const CMD_LEARN_SKILL = 'CMD_LEARN_SKILL';
/**
 * Spend a skill point to raise a learned skill by one rank.
 *
 * The **same** currency `CMD_LEARN_SKILL` spends: learning is buying rank 1 and
 * levelling is buying rank n+1, so the two are one purchase with two names and the
 * server's invariant stays a plain sum.
 */
export const CMD_LEVEL_SKILL = 'CMD_LEVEL_SKILL';
/** Spend one passive point on one node of the generic pool. */
export const CMD_ALLOCATE_PASSIVE = 'CMD_ALLOCATE_PASSIVE';
/** Refund every allocated passive point at once. All or nothing. */
export const CMD_RESPEC_PASSIVES = 'CMD_RESPEC_PASSIVES';
/** Drink one charge from the potion belt. No payload — the belt holds one thing. */
export const CMD_USE_POTION = 'CMD_USE_POTION';
/**
 * Stand at one of the town's workshops.
 *
 * **The client names a kind, never a position** — where the Forge stands belongs to
 * the layout, so a modified client cannot choose the coordinates it lands on and
 * cannot use this outside the hub at all.
 *
 * Deliberately **not predicted**: a teleport is far more than the reconciler's snap
 * distance from wherever the player was, so the server's next position snaps rather
 * than easing. One tick of latency, and no guessing at a move the client has no
 * business predicting.
 */
export const CMD_TELEPORT_TO_STATION = 'CMD_TELEPORT_TO_STATION';
/**
 * "I have walked to this workshop; let me trade here."
 *
 * The range check the shopfronts never had. An artisan is scenery the client builds
 * from the ZONE frame's station list, so until this existed nothing server-side could
 * tell a player standing at the Forge from one who had never left the gate.
 *
 * It moves nobody — that is the whole difference from `CMD_TELEPORT_TO_STATION`, which
 * is the hub spec's answer to navigation friction and stands you at a workshop from
 * anywhere in town. Two gestures, not two ways to do one thing.
 *
 * **Answered locally and still sent**, the rule a predicted dodge follows: the panel
 * opens the moment the walk arrives, and `EVENT_STATION_REFUSED` is the server closing
 * it again when it disagrees. Success is silent.
 */
export const CMD_OPEN_STATION = 'CMD_OPEN_STATION';
/**
 * Step through the gate the player is standing at — a portal, or the zone's exit.
 *
 * **This replaced walking into them.** Both used to be *places*: standing on one
 * triggered it server-side at the end of `Step`. A gate you can trigger by accident is a
 * gate you cannot stand next to — which is why the hub's entry sat at the far end of town
 * from its portals — and a place that acts on proximity gives a player no way to say *not
 * yet*. A gate is a click target now, like an artisan: the click walks you there and this
 * is sent on arrival. The server still measures the distance.
 */
export const CMD_USE_GATE = 'CMD_USE_GATE';
/**
 * Leave a carried item on the ground where the player stands.
 *
 * **The only way out of the bag that is not a trade.** Scrapping needs a workshop and is
 * town-only, depositing needs the vault, and a bag that fills mid-dive otherwise has one
 * answer: walk home. This is the answer for the thing that is not worth the walk.
 *
 * A uuid and nothing else — *where* it lands is the server's, from its own copy of the
 * player's position. Refused for a padlocked item, which is what a padlock is for.
 */
export const CMD_DROP_ITEM = 'CMD_DROP_ITEM';
/**
 * Give a batch of carried items to a merchant for gold.
 *
 * Skips locked items rather than refusing
 * the batch: a padlock is a mark on the *item*, not an objection to the gesture — which is
 * what makes "sell all unlocked" a button rather than a chore. Town only.
 */
export const CMD_SELL_ITEMS = 'CMD_SELL_ITEMS';
/**
 * Take one offer off the merchant's shelf.
 *
 * An offer id, never an item id or a price: what is for sale and what it costs are the
 * server's, stamped when the shop was rolled. A client naming a price would be naming the
 * outcome.
 */
export const CMD_BUY_ITEM = 'CMD_BUY_ITEM';
/**
 * Bank gold, or take it back out — **positive deposits, negative withdraws**.
 *
 * One command rather than two, because they are one question: how much should I be
 * carrying. Town only, and refused for more than the source holds — the server owns both
 * balances, so an amount the client does not have is told no rather than trusted.
 */
export const CMD_MOVE_GOLD = 'CMD_MOVE_GOLD';

/**
 * Party and chat.
 *
 * **An invite names a player, not an id.** Ids appear on no screen, so a client inviting by
 * id could only have obtained one by enumerating — which naming by name prevents outright.
 *
 * A chat message carries no sender: the server stamps the name from the world, because a
 * client that supplied its own could speak as anybody.
 */
export const CMD_PARTY_INVITE = 'CMD_PARTY_INVITE';
export const CMD_PARTY_RESPOND = 'CMD_PARTY_RESPOND';
export const CMD_PARTY_LEAVE = 'CMD_PARTY_LEAVE';
export const CMD_PARTY_KICK = 'CMD_PARTY_KICK';
/**
 * Teleport to a party member, wherever they are.
 *
 * **This is how a party plays together**, and it replaced the design spec's post-map vote
 * (owner's call): everybody dives into their own zone alone, and anyone who wants company
 * clicks the portrait of the person they want to be with. Nobody waits on a ready check,
 * alone and together are the same feature, and *I do not want company* is said by leaving
 * the party rather than by declining a prompt.
 */
export const CMD_PARTY_JOIN = 'CMD_PARTY_JOIN';
export const CMD_CHAT_SEND = 'CMD_CHAT_SEND';
export const CMD_SLOT_SKILL = 'CMD_SLOT_SKILL';
export const CMD_CAST_SKILL = 'CMD_CAST_SKILL';
/** Swing at whatever is nearest a point, without walking there — the shift-click. */
export const CMD_ATTACK_GROUND = 'CMD_ATTACK_GROUND';
/**
 * The dodge roll: the universal action on the spacebar, from
 * `doc/grind/design/DODGE.md`.
 *
 * **The client names a direction, never a distance.** How far a roll travels is an
 * outcome — a client that chose it could cross a room — so the payload is an aim point
 * and the server does the rest. Same split as `CMD_ATTACK_GROUND`.
 *
 * Unlike a teleport this one **is** predicted, and has to be: four units in 0.3 s is
 * well past `RECONCILE_SNAP_DISTANCE`, so an unpredicted roll snaps on every press.
 * See `PlayerPredictor.dodge`.
 */
export const CMD_DODGE = 'CMD_DODGE';

/**
 * The four developer commands, from Phase 1 of the roadmap.
 *
 * **Every one is gated server-side**, by `test_mode.commands` in
 * `configs/leagues.yaml`: `World.Apply` refuses any of them that the active league
 * does not permit, in the same place it refuses a dead player's commands. The client
 * is told whether the panel exists — `WELCOME.testMode` — and hides it when it does
 * not, but that is an affordance and not the rule. Sending one anyway earns an
 * `EVENT_TEST_REFUSED` with `NOT_PERMITTED`.
 *
 * `GRIND_LEAGUE=league_playtest` is the one way to turn them on.
 */
export const CMD_TEST_SPAWN_ITEM = 'CMD_TEST_SPAWN_ITEM';
/**
 * Jump to a level. **Upward only** — the server applies it as an experience award, so
 * the points come from levelling and the allocation invariants hold by construction.
 */
export const CMD_TEST_SET_LEVEL = 'CMD_TEST_SET_LEVEL';
/** Award experience: the same door as a set level, in the finer unit. */
export const CMD_TEST_GRANT_XP = 'CMD_TEST_GRANT_XP';
/**
 * Fill the potion belt outside town.
 *
 * The one developer command that suspends a design rule — only town restocks — which
 * is exactly why it is a server command and not a client-side number.
 */
export const CMD_TEST_REFILL_BELT = 'CMD_TEST_REFILL_BELT';

export const FRAME_WELCOME = 'WELCOME';
export const FRAME_SNAPSHOT = 'SNAPSHOT';
export const FRAME_ZONE = 'ZONE';
export const FRAME_INVENTORY = 'INVENTORY';
/** The party, its members and their vitals. Private, and sent only when membership changes. */
export const FRAME_PARTY = 'PARTY';
/** Who is online, and any invite waiting to be answered. */
export const FRAME_SOCIAL = 'SOCIAL';
/** A batch of chat lines, drained on the tick that produced them. */
export const FRAME_CHAT = 'CHAT';
export const FRAME_CHARACTER = 'CHARACTER';

export const EVENT_ENTITY_DAMAGED = 'EVENT_ENTITY_DAMAGED';
export const EVENT_ENTITY_DIED = 'EVENT_ENTITY_DIED';
export const EVENT_PLAYER_DIED = 'EVENT_PLAYER_DIED';
export const EVENT_LOOT_DROPPED = 'EVENT_LOOT_DROPPED';
export const EVENT_LOOT_TAKEN = 'EVENT_LOOT_TAKEN';
export const EVENT_ZONE_CHANGED = 'EVENT_ZONE_CHANGED';
export const EVENT_LEVEL_UP = 'EVENT_LEVEL_UP';
/**
 * A cast **began**. `sourceId` is the skill id, `aimX`/`aimZ` where it points.
 *
 * The pair to `EVENT_SKILL_CAST`, and it exists because a cast is no longer one
 * instant: with `castTimeSec` there is a beginning the animation plays from and an
 * end where the outcome lands, and for an ultimate those are 25 ticks apart. This one
 * starts the body moving and throws nothing.
 *
 * Emitted for an instant cast too, in the same tick as the resolution. One rule each
 * beats a client working out which of two meanings a single event had.
 */
export const EVENT_SKILL_CAST_STARTED = 'EVENT_SKILL_CAST_STARTED';
/**
 * A cast **resolved**: the damage is applied as of this tick.
 *
 * It used to fire when the command arrived, because a cast resolved instantly. With a
 * cast time it fires at the *end* of the window, which is what keeps the projectile
 * honest — a bolt flies for a fixed 0.18 s, so launching one at the start of a 0.85 s
 * wind-up would have it arrive long before the thing it represents.
 *
 * An interrupted cast never emits it, which is the whole of "no bolt for a cast that
 * did not happen".
 */
export const EVENT_SKILL_CAST = 'EVENT_SKILL_CAST';
/**
 * Something hit an *area*: a player's blast or a monster's slam.
 *
 * **One event for both**, because they are one thing on screen — a ring at a point,
 * expanding once. It carries where and how wide and nothing about damage: the numbers
 * arrive as ordinary `EVENT_ENTITY_DAMAGED` per victim, which is what already puts a
 * figure over each head. Repeating the damage here would be a second source of truth for
 * the number the player reads.
 */
export const EVENT_AREA_STRIKE = 'EVENT_AREA_STRIKE';
/** A cast the server refused, with `reason` saying which rule stopped it. */
export const EVENT_SKILL_REFUSED = 'EVENT_SKILL_REFUSED';
/**
 * A rank the server refused, with `reason` saying which rule stopped it and
 * `sourceId` naming the skill.
 *
 * Separate from `EVENT_SKILL_REFUSED` because it is answered somewhere else: a
 * refused *cast* belongs in the action bar's corner and carries a bar slot in
 * `amount`, a refused *rank* belongs in the skills panel beside the button that
 * asked.
 */
export const EVENT_SKILL_LEVEL_REFUSED = 'EVENT_SKILL_LEVEL_REFUSED';
export const EVENT_XP_GAINED = 'EVENT_XP_GAINED';
/** A charge was drunk; `amount` is the health the **server** restored. */
export const EVENT_POTION_USED = 'EVENT_POTION_USED';
/** A draught the server refused, with `reason` saying which rule stopped it. */
export const EVENT_POTION_REFUSED = 'EVENT_POTION_REFUSED';
/**
 * A batch scrap completed: `amount` is the scrap paid, `count` the items consumed.
 *
 * `count` exists because a batch is legally **partial** — a locked item is skipped
 * rather than failing the whole thing — so the client must not assume everything it
 * named is gone. The INVENTORY frame that follows is the truth about what remains.
 */
/** A batch that scrapped nothing at all, with `reason` saying why. */
export const EVENT_SELL_REFUSED = 'EVENT_SELL_REFUSED';
/**
 * A `CMD_OPEN_STATION` the server refused, with `reason` saying which rule stopped it
 * and `station` naming the workshop it was about.
 *
 * There is no matching "opened" event, deliberately: the client already opened the
 * panel, so the server speaks only when it disagrees. `station` rather than "whichever
 * panel is up" because a refusal can arrive a tab-switch late, and closing the wrong
 * shop would be worse than the refusal it is reporting.
 */
export const EVENT_STATION_REFUSED = 'EVENT_STATION_REFUSED';
/**
 * A `CMD_USE_GATE` the server refused, with `reason` saying which rule stopped it.
 *
 * No matching "used" event, for the reason there is no "opened" one: success arrives as
 * `EVENT_ZONE_CHANGED` and a whole new `ZONE` frame, which is not something a client can
 * miss.
 */
export const EVENT_GATE_REFUSED = 'EVENT_GATE_REFUSED';
/** A completed sale: `amount` is the gold paid, `count` how many items went. */
export const EVENT_ITEMS_SOLD = 'EVENT_ITEMS_SOLD';
/** A completed purchase, with `amount` as the gold spent. */
export const EVENT_ITEM_BOUGHT = 'EVENT_ITEM_BOUGHT';
/** A `CMD_BUY_ITEM` the server refused, with `reason` saying which rule stopped it. */
export const EVENT_BUY_REFUSED = 'EVENT_BUY_REFUSED';
/**
 * A placement the server would not make: a `CMD_MOVE_ITEM`, or a pickup with nowhere
 * to go. `reason` says which rule stopped it and `container` says where it was headed.
 *
 * **A pickup emits it too**, which is why it exists at all. Refusing a full bag used to
 * be silent — a player pressing `F` over a pile of loot got nothing and no explanation
 * — and footprints made that worse rather than better: the bag can now refuse a
 * greatsword while accepting the ring beside it, so "full" is not even the right word.
 *
 * No matching success event: a placement that worked arrives in the INVENTORY frame,
 * which every panel is redrawing from anyway.
 */
export const EVENT_ITEM_MOVE_REFUSED = 'EVENT_ITEM_MOVE_REFUSED';
/**
 * A roll started. `aimX`/`aimZ` carry the direction as a unit vector.
 *
 * Sent to everyone, like `EVENT_SKILL_CAST`, so a spectator can animate another
 * player's roll — the snapshot's `DODGING` state says one is happening but not which
 * way, and interpolated positions arrive two ticks behind.
 */
export const EVENT_DODGED = 'EVENT_DODGED';
/** A roll the server refused, with `reason` saying which rule stopped it. */
export const EVENT_DODGE_REFUSED = 'EVENT_DODGE_REFUSED';
/**
 * A hit that landed inside a player's i-frames and was refused. `amount` is the
 * damage they did **not** take.
 *
 * The only event that reports something *not* happening, and it exists because a
 * swing that silently does nothing is indistinguishable from a bug. It is how the
 * player learns the timing.
 */
export const EVENT_HIT_AVOIDED = 'EVENT_HIT_AVOIDED';
/**
 * A developer command the server refused, with `reason` saying which rule stopped it.
 *
 * The fourth refusal, and the one whose reasons are mostly about *content*: a base
 * that does not drop this deep, a unique asked for at Magic. Being told beats
 * concluding the button is broken, which is the whole reason this family exists.
 * There is no matching "applied" event — a spawn arrives in the INVENTORY frame and a
 * level in the CHARACTER frame, so success already had a channel.
 */
export const EVENT_TEST_REFUSED = 'EVENT_TEST_REFUSED';

export type EntityKind = 'player' | 'mob';

/**
 * The states the server may report an entity in.
 *
 * `CASTING` is a player inside a skill's cast window, and it arrived with cast times:
 * before them a cast resolved in the tick its command arrived and the animation was
 * a pure client overlay, so there was no state to report. Now the simulation *holds*
 * the body for up to a second — refusing to walk it, refusing to swing it — which is
 * exactly the kind of thing a state is for. Since Phase 29.2 **nearly every skill is windowed** —
 * the 85 that merely lacked a `cast_time_sec` now have a derived one — so only a genuinely
 * `INSTANT` skill has no state to be in.
 *
 * `WIND_UP` and `RECOVERY` (Phase 29.5) are the two halves of a swing, for players and monsters
 * alike: *damage is coming, you may still step out of it* against *the blow landed and this entity
 * cannot act yet*. They are deliberately distinct from `ATTACKING`, which only means an attack
 * order is standing and stays true across both, and from `CASTING`, which is a skill window.
 * `animState.ts` maps both to the attack clip — the split is for the HUD, not the skeleton.
 */
export type EntityState =
  | 'IDLE'
  | 'MOVING'
  | 'CHASING'
  | 'ATTACKING'
  | 'DEAD'
  | 'DODGING'
  | 'CASTING'
  // A CHANNEL_MOVE skill running — Whirlwind spinning. Phase 30.4. **Its own state rather than
  // CASTING**, because the body has to spin and `state` is the only thing a snapshot tells other
  // players about what someone is doing: it carries no skill id, and the cast event reaches only
  // the caster.
  | 'CHANNELLING'
  | 'WIND_UP'
  | 'RECOVERY';

/**
 * The visual class of a worn chest piece, from `armor_category` in `items.yaml`.
 *
 * **Purely visual.** There is no mitigation formula anywhere in the design, and
 * armour is summed from gear and read by nothing — a class changes what a character
 * looks like and nothing else. `NONE` is the catalogue's value for anything that is
 * not armour and is never sent: a bare chest has no `armour` field at all.
 */
export type ArmourClass = 'CLOTH' | 'LEATHER' | 'PLATE';

/**
 * What a player's off hand holds, from `offhand_type` in `items.yaml` and `uniques.yaml` —
 * or `DAGGER`, the one weapon family the server lets into that slot. One model each in
 * `game/models/offhand.ts`. Phase 41.
 *
 * **Purely visual**, like `ArmourClass`: a buckler's block chance is in its `base_stats`,
 * and the silhouette implies no number. Absent means the kite shield the crusader always
 * carried, so a client that has not been told draws what it always drew.
 */
export type OffHandClass = 'BUCKLER' | 'SHIELD' | 'TOME' | 'ORB' | 'DAGGER';

/**
 * The weapon family in a player's main hand, from `weapon_type` in `items.yaml` and
 * `uniques.yaml`. One silhouette each — see `game/models/weapons.ts`.
 *
 * **Purely visual**, the same way an armour class is: damage, speed and crit come
 * from the item's `base_stats`, and no shape here implies any of them.
 *
 * The wire carries the *type*, not the item's `visuals.mesh_id`, because the twenty-five
 * unique weapons each carry a per-item mesh id of their own — keying the model off
 * that would need twenty-five builders or a fallback that made every unique weapon look
 * wrong. The type is the closed vocabulary both catalogues already validate, and it
 * is what decides a silhouette. Absent for an empty main hand and for every mob.
 */
export type WeaponClass =
  | 'SWORD_1H'
  | 'SWORD_2H'
  | 'AXE_1H'
  | 'AXE_2H'
  | 'MACE_1H'
  | 'MACE_2H'
  | 'DAGGER'
  | 'WAND'
  | 'STAFF'
  | 'BOW';

/**
 * The town's five workshops, from HUB-ZONES-SCRAP-UI.md.
 *
 * Each covers a material discipline, and **only scrapping works**: buying and crafting
 * need vendor stock and recipes that do not exist, so those parts of a station's panel
 * render a designed empty state rather than a shop with nothing in it.
 */
export type StationKind =
  | 'FORGE'
  | 'TANNERY'
  | 'ATELIER'
  | 'WOODWORKS'
  | 'ARCANE_VAULT'
  /**
   * The stash, and **not a workshop**.
   *
   * Storing your gear and enchanting a ring are different errands, and they were the same
   * place: the Arcane Vault's panel held both the jewellery bench and every item a player
   * owned, which read as the workshop *being* the storage. It is a `StationKind` because a
   * station is simply "a place in town with a panel" — walking to one, teleporting to one
   * and opening its panel is machinery that already exists.
   */
  | 'STASH';

/** One workshop standing in the town. */
export interface ZoneStation {
  kind: StationKind;
  col: number;
  row: number;
  at: ZonePoint;
}

// ── Client → server ─────────────────────────────────────────────────────────

export type Command =
  | {
      type: typeof CMD_MOVE_TO;
      payload: { targetX: number; targetZ: number; seq: number; chase?: boolean };
    }
  | { type: typeof CMD_ATTACK_TARGET; payload: { targetId: string } }
  | { type: typeof CMD_RESPAWN }
  | { type: typeof CMD_PICKUP; payload: { lootId: string } }
  | { type: typeof CMD_BREAK; payload: { fixtureId: number } }
  | { type: typeof CMD_LOCK_ITEM; payload: { uuid: string; locked: boolean } }
  | {
      type: typeof CMD_MOVE_ITEM;
      payload: { uuid: string; container: ContainerId; col: number; row: number };
    }
  | { type: typeof CMD_ENTER_ZONE; payload: { enemyLevel: number } }
  | { type: typeof CMD_RETURN_TO_HUB }
  | { type: typeof CMD_EQUIP_ITEM; payload: { uuid: string; slot: DollSlot } }
  | { type: typeof CMD_UNEQUIP_ITEM; payload: { slot: DollSlot } }
  | { type: typeof CMD_ALLOCATE_ATTRIBUTE; payload: { attribute: AttributeKey } }
  | { type: typeof CMD_LEARN_SKILL; payload: { skillId: string } }
  | { type: typeof CMD_LEVEL_SKILL; payload: { skillId: string } }
  | { type: typeof CMD_ALLOCATE_PASSIVE; payload: { passiveId: string } }
  | { type: typeof CMD_RESPEC_PASSIVES }
  | { type: typeof CMD_USE_POTION }
  | { type: typeof CMD_TELEPORT_TO_STATION; payload: { station: StationKind } }
  | { type: typeof CMD_OPEN_STATION; payload: { station: StationKind } }
  | { type: typeof CMD_USE_GATE; payload: { gate: GateKind } }
  | { type: typeof CMD_DROP_ITEM; payload: { uuid: string } }
  | { type: typeof CMD_SELL_ITEMS; payload: { uuids: string[] } }
  | { type: typeof CMD_BUY_ITEM; payload: { offerId: string } }
  | { type: typeof CMD_MOVE_GOLD; payload: { amount: number } }
  // Party and chat. The invite names a *name* because ids are on no screen; the kick names
  // an id because the client already has one from its own party frame.
  | { type: typeof CMD_PARTY_INVITE; payload: { name: string } }
  | { type: typeof CMD_PARTY_RESPOND; payload: { accept: boolean } }
  | { type: typeof CMD_PARTY_LEAVE; payload: Record<string, never> }
  | { type: typeof CMD_PARTY_KICK; payload: { playerId: string } }
  | { type: typeof CMD_PARTY_JOIN; payload: { playerId: string } }
  | { type: typeof CMD_CHAT_SEND; payload: { channel: ChatChannel; text: string } }
  | { type: typeof CMD_SLOT_SKILL; payload: { slot: number; skillId: string } }
  | {
      type: typeof CMD_CAST_SKILL;
      payload: { slot: number; aimed?: boolean; x?: number; z?: number };
    }
  | { type: typeof CMD_ATTACK_GROUND; payload: { x: number; z: number } }
  | {
      type: typeof CMD_DODGE;
      payload?: { aimed?: boolean; x?: number; z?: number; predicted?: boolean };
    }
  | {
      type: typeof CMD_TEST_SPAWN_ITEM;
      payload: { baseId: string; rarity: ItemRarity; itemLevel: number };
    }
  | { type: typeof CMD_TEST_SET_LEVEL; payload: { level: number } }
  | { type: typeof CMD_TEST_GRANT_XP; payload: { amount: number } }
  | { type: typeof CMD_TEST_REFILL_BELT };

/** The four attributes, spelled as the server and the YAML spell them. */
export type AttributeKey = 'str' | 'dex' | 'int' | 'end';

/**
 * `seq` is the client's own counter for this intent. The server echoes the highest
 * it has applied as `ackSeq`, which is how a predicting client knows whether the
 * position it is being sent already reflects this order.
 */
/**
 * Walk to a point.
 *
 * `chase` marks the leg as part of a walk toward the player's own attack target, which
 * is the one move order that does **not** clear it server-side. An ordinary click on
 * the floor still does, and must: walking away is how a player disengages.
 *
 * Omitted rather than sent as `false`, so the overwhelming majority of walks are byte
 * for byte the frame they always were — the convention `crit` follows on an event.
 */
export function moveTo(
  targetX: number,
  targetZ: number,
  seq: number,
  chase = false,
): Command {
  return {
    type: CMD_MOVE_TO,
    payload: chase ? { targetX, targetZ, seq, chase: true } : { targetX, targetZ, seq },
  };
}

export function attackTarget(targetId: string): Command {
  return { type: CMD_ATTACK_TARGET, payload: { targetId } };
}

/**
 * Revives a dead player — **in town**, not where they fell.
 *
 * It is the only command a corpse may send. Waking at the zone entry used to be the
 * other option, beside a pack that does not go back to sleep, which is why the death
 * screen needed a retreat button next to this one; going home merges the two into
 * the single way out that the death screen now offers.
 */
export function respawn(): Command {
  return { type: CMD_RESPAWN };
}

export function pickup(lootId: string): Command {
  return { type: CMD_PICKUP, payload: { lootId } };
}

export function breakFixture(fixtureId: number): Command {
  return { type: CMD_BREAK, payload: { fixtureId } };
}

export function lockItem(uuid: string, locked: boolean): Command {
  return { type: CMD_LOCK_ITEM, payload: { uuid, locked } };
}

/**
 * Move an item to an exact cell of a container.
 *
 * `col`/`row` are the item's **top-left** cell; the server derives the rest from the
 * footprint. No size is sent — a client that could state one could state a 1x1
 * greatsword.
 *
 * Naming the container the item is already in is a rearrangement, and dropping it back
 * on its own square is legal (that is what a cancelled drag looks like). Naming the
 * other one is a deposit or a withdrawal, and the server refuses either outside town.
 */
export function moveItem(
  uuid: string,
  container: ContainerId,
  col: number,
  row: number,
): Command {
  return { type: CMD_MOVE_ITEM, payload: { uuid, container, col, row } };
}

/**
 * Wear a carried item.
 *
 * The slot is explicit rather than derived from the item: a ring fits either
 * hand and only the player knows which they meant.
 */
export function equipItem(uuid: string, slot: DollSlot): Command {
  return { type: CMD_EQUIP_ITEM, payload: { uuid, slot } };
}

/** Take an item off the paper doll and back into the bag. */
export function unequipItem(slot: DollSlot): Command {
  return { type: CMD_UNEQUIP_ITEM, payload: { slot } };
}

/** Spend one attribute point. */
export function allocateAttribute(attribute: AttributeKey): Command {
  return { type: CMD_ALLOCATE_ATTRIBUTE, payload: { attribute } };
}

/** Spend a skill point to unlock a skill permanently. */
export function learnSkill(skillId: string): Command {
  return { type: CMD_LEARN_SKILL, payload: { skillId } };
}

/**
 * Spend a skill point to raise a learned skill by one rank.
 *
 * One rank per command, never a target rank: points leave the pocket one at a time,
 * which is what holds the server's allocation invariant by construction.
 */
export function levelSkill(skillId: string): Command {
  return { type: CMD_LEVEL_SKILL, payload: { skillId } };
}

/** Spend one passive point on one node. Refused server-side if it cannot go there. */
export function allocatePassive(passiveId: string): Command {
  return { type: CMD_ALLOCATE_PASSIVE, payload: { passiveId } };
}

/**
 * Refund every allocated passive point.
 *
 * All or nothing, matching the server: refunding one rank would need a record of
 * what each rank cost.
 */
export function respecPassives(): Command {
  return { type: CMD_RESPEC_PASSIVES };
}

/**
 * Drink one charge from the potion belt.
 *
 * The server decides whether anything happens and how much it heals — it checks the
 * charge, the shared cooldown and the player's health. **The client never heals
 * itself**; it sends the keypress and waits for EVENT_POTION_USED.
 */
export function usePotion(): Command {
  return { type: CMD_USE_POTION };
}

/** Stand at one of the town's workshops. Refused outside the hub. */
export function teleportToStation(station: StationKind): Command {
  return { type: CMD_TELEPORT_TO_STATION, payload: { station } };
}

/**
 * Ask to trade at the workshop the player is standing at.
 *
 * Moves nobody. Sent alongside opening the panel, never instead of it — the answer the
 * client already holds is worth more than the round trip, and the server's refusal is
 * what closes the panel again when the two disagree.
 */
export function openStation(station: StationKind): Command {
  return { type: CMD_OPEN_STATION, payload: { station } };
}

/**
 * The two gates, mirroring `protocol.Gates` on the server.
 *
 * A *kind*, never an id: there is only ever one portal open, and which way it leads is
 * the world's business rather than this client's. Naming a portal by id would let a stale
 * client ask for a gate that closed two ticks ago and get a more confusing answer than
 * "there is no gate".
 */
export const GATE_PORTAL = 'PORTAL';
export const GATE_EXIT = 'EXIT';

export const GATES = [GATE_PORTAL, GATE_EXIT] as const;

export type GateKind = (typeof GATES)[number];

export function useGate(gate: GateKind): Command {
  return { type: CMD_USE_GATE, payload: { gate } };
}

export function dropItem(uuid: string): Command {
  return { type: CMD_DROP_ITEM, payload: { uuid } };
}

export function sellItems(uuids: string[]): Command {
  return { type: CMD_SELL_ITEMS, payload: { uuids } };
}

export function buyItem(offerId: string): Command {
  return { type: CMD_BUY_ITEM, payload: { offerId } };
}

export function moveGold(amount: number): Command {
  return { type: CMD_MOVE_GOLD, payload: { amount } };
}


/**
 * Invite somebody to the party, by the name on their character.
 *
 * The server refuses an unknown name, yourself, somebody already grouped and a full party —
 * each with its own reason, because they are four different things to do about it.
 */
export function invitePlayer(name: string): Command {
  return { type: CMD_PARTY_INVITE, payload: { name } };
}

/** Answer the invite that is waiting. There is at most one, so it names none. */
export function respondToInvite(accept: boolean): Command {
  return { type: CMD_PARTY_RESPOND, payload: { accept } };
}

export function leaveParty(): Command {
  return { type: CMD_PARTY_LEAVE, payload: {} };
}

/** Remove a member. Leader only — the server checks, this is only the affordance. */
export function kickFromParty(playerId: string): Command {
  return { type: CMD_PARTY_KICK, payload: { playerId } };
}

/**
 * Go to a party member, wherever they are.
 *
 * Names a **member**, not a zone: a player asks to be *with somebody*, so there is nothing
 * here a client could invent to reach a zone it cannot see. The server refuses if they are not
 * in your party, or if you are dead — arriving as a corpse in somebody else's fight helps
 * nobody.
 */
export function joinPartyMember(playerId: string): Command {
  return { type: CMD_PARTY_JOIN, payload: { playerId } };
}

/**
 * Say something on a channel.
 *
 * No sender: the server stamps the name from the world, because a client that supplied its
 * own could speak as anybody.
 */
export function sendChat(channel: ChatChannel, text: string): Command {
  return { type: CMD_CHAT_SEND, payload: { channel, text } };
}

/** Put a learned skill on the action bar. An empty id clears the slot. */
export function slotSkill(slot: number, skillId: string): Command {
  return { type: CMD_SLOT_SKILL, payload: { slot, skillId } };
}

/** Fire whatever is in an action-bar slot. */
/** Swing at whatever is nearest a point. Holding shift, in other words. */
export function attackGround(x: number, z: number): Command {
  return { type: CMD_ATTACK_GROUND, payload: { x, z } };
}

/**
 * Roll, optionally toward a point.
 *
 * With no aim the server rolls along the player's facing — a legal roll, which is why
 * the payload is optional and the whole frame may be `{"type":"CMD_DODGE"}`.
 */
export function dodge(aim?: { x: number; z: number }, predicted = false): Command {
  // `predicted` is **diagnostic only** and the server must never gate behaviour on it —
  // a modified client can say anything. It exists so that a refusal the client did *not*
  // expect can be told apart from one it did: the client now checks the same cooldown and
  // stamina the server does, so a refused roll it predicted means the two disagree, which
  // is exactly the desync worth logging.
  return aim
    ? { type: CMD_DODGE, payload: { aimed: true, x: aim.x, z: aim.z, predicted } }
    : { type: CMD_DODGE, payload: { predicted } };
}

export function castSkill(slot: number, aim?: { x: number; z: number }): Command {
  // `aimed` is an explicit flag rather than testing for (0,0): the origin is a
  // perfectly good place to stand, and it is the middle of every zone.
  return aim
    ? { type: CMD_CAST_SKILL, payload: { slot, aimed: true, x: aim.x, z: aim.z } }
    : { type: CMD_CAST_SKILL, payload: { slot } };
}

/** The [ GRIND ] portal: leave town for a fresh zone at this enemy level. */
export function enterZone(enemyLevel: number): Command {
  return { type: CMD_ENTER_ZONE, payload: { enemyLevel } };
}

/**
 * Opens a town portal beside the player. **It does not move them.**
 *
 * The portal is a place: walking into it is what goes home, checked server-side at
 * the end of `Step` exactly as the zone's exit is. So this is a cast, not a
 * teleport — the answer arrives as a portal in the next snapshot, and the walk is
 * the player's own.
 *
 * Refused in town, refused on a half-second double-press guard, and refused for a
 * corpse, which cannot walk: a dead player's one exit is `respawn()`, which wakes
 * them in town.
 */
export function returnToHub(): Command {
  return { type: CMD_RETURN_TO_HUB };
}

/**
 * The developer commands.
 *
 * Ordinary command builders, with nothing conditional in them: the panel that sends
 * them is hidden without test mode, and the *server* is what refuses them. A client-side
 * check here would be a cheat vector wearing a coat, and would also be the wrong place
 * for the answer — the reasons are content facts the server holds.
 */
export function testSpawnItem(baseId: string, rarity: ItemRarity, itemLevel: number): Command {
  return { type: CMD_TEST_SPAWN_ITEM, payload: { baseId, rarity, itemLevel } };
}

export function testSetLevel(level: number): Command {
  return { type: CMD_TEST_SET_LEVEL, payload: { level } };
}

export function testGrantXP(amount: number): Command {
  return { type: CMD_TEST_GRANT_XP, payload: { amount } };
}

export function testRefillBelt(): Command {
  return { type: CMD_TEST_REFILL_BELT };
}

export function encodeCommand(cmd: Command): string {
  return JSON.stringify(cmd);
}

// ── Server → client ─────────────────────────────────────────────────────────

export interface WelcomePayload {
  playerId: string;
  tickRate: number;
  worldSize: number;
  /** World units per second. The client runs the same movement rule to predict. */
  playerSpeed: number;
  /**
   * How long a walk takes to reach `playerSpeed`, and how long it takes to come to
   * rest, in seconds. Here for the same reason `playerSpeed` is: the client
   * integrates the ramp itself, and a predictor that launched at full speed while the
   * server ramped would be corrected on the opening frames of every walk.
   *
   * A shape, not a rate, and nothing scales it with the character — so unlike the walk
   * speed there is no CHARACTER override to prefer. Zero means the ramp is off.
   */
  walkRampTime: number;
  /**
   * The dodge roll's trajectory, so the client can predict one.
   *
   * Both are needed for that and neither scales with the character, which is why they
   * ride WELCOME while the roll's cooldown, cost and i-frame window ride CHARACTER.
   * None of the latter three is needed to predict a *position*.
   */
  dodgeDistance: number;
  dodgeDuration: number;
  /**
   * How close a swing reaches, in world units.
   *
   * Here because **the client routes a chase**: clicking a monster turns into a path
   * and a sequence of legs, and only the thing issuing the legs can know where to stop
   * walking. A guessed reach either stops short — a chase that walks up and never
   * swings — or walks into the monster and is pushed back out by collision every tick.
   *
   * Read, never enforced. Whether a swing lands is `resolvePlayerAttack`'s decision
   * and nothing here changes that.
   */
  playerAttackRange: number;
  /**
   * How close counts as standing at a workshop, in world units.
   *
   * The client needs it to know when its walk to an artisan has arrived; the server
   * re-checks it on `CMD_OPEN_STATION`, because the stall is geometry the client draws
   * and the distance is the only thing that makes it a place.
   */
  stationRange: number;
  /**
   * How close a player must stand to each gate for `CMD_USE_GATE` to be granted.
   *
   * On the wire for the reason `stationRange` is: a gate is a click target, the client
   * routes the walk, and only the client knows where to stop. The server re-checks both.
   * **Two numbers rather than one** — the exit's is the more generous, and one figure for
   * both would either ask too early at the exit or walk needlessly far into a portal.
   */
  portalEntryRange: number;
  exitRange: number;
  /**
   * How close a player must stand to collect a drop.
   *
   * Sent because **the client routes the walk** and is therefore the only half that knows
   * where it should stop. Clicking a drop across the room used to send `CMD_PICKUP`
   * immediately, which the server refused for range — so the item sat there and the click
   * did nothing.
   */
  pickupRange: number;
  /**
   * The whole active skill catalogue, sent once. Static content, so it rides
   * WELCOME rather than being repeated in every CHARACTER frame.
   */
  skills: SkillInfo[];
  /** Progression arithmetic from skills.yaml, so the client restates nothing. */
  attributePointsPerLevel: number;
  skillPointsPerLevel: number;
  passivePointsPerLevel: number;
  /**
   * The whole generic passive pool, sent once for the same reason the skills are.
   * The CHARACTER frame carries only the ranks.
   */
  passives: PassiveInfo[];
  /**
   * The developer panel, present **only when the active league permits a developer
   * command** and absent otherwise — which is what lets the HUD hide the panel instead
   * of offering a button the server would refuse.
   *
   * The client must not infer any of this. Which commands are unlocked is a league
   * parameter, and the spawnable catalogue is the server's content: a panel with its own
   * list would drift the moment a base was added, and one with a free-text field would
   * be asking a developer to recall `items.yaml` from memory.
   *
   * An affordance, never the rule — `World.Apply` re-checks the gate on every command,
   * so a client that fabricates this field gains nothing at all.
   */
  testMode?: TestModeInfo;
}

/** What the developer panel needs in order to offer only what the server will grant. */
export interface TestModeInfo {
  /** The permitted commands, by wire type. The panel greys anything absent. */
  commands: string[];
  /** Every base item and unique template a spawn may name. */
  bases: TestSpawnBase[];
  /** The progression cap, so the level field can bound itself. */
  maxLevel: number;
}

/** One thing a developer spawn may name. */
export interface TestSpawnBase {
  baseId: string;
  name: string;
  slot: string;
  /**
   * The item level the catalogue starts dropping it at. Below it the generator cannot
   * produce it and the spawn is refused, so the panel needs the number to say so
   * without a round trip.
   */
  minLevel: number;
  /**
   * A unique template, which may only be spawned at `UNIQUE` — and conversely a
   * `UNIQUE` spawn must name one of these. A unique's stats are its identity, so there
   * is no "roll this base as a unique".
   */
  unique?: boolean;
}

/** One passive node's static definition, mirrored from passives.yaml. */
export interface PassiveInfo {
  passiveId: string;
  name: string;
  category: string;
  maxRank: number;
  levelReq: number;
  statGates: Attributes;
  /** What one rank grants, in catalogue order so lines never reshuffle. */
  statsPerRank: PassiveStat[];
  description: string;
  /**
   * Names this node's art, from `visual_assets.icon_id`. Absent, or naming a file this
   * build has not got, draws the node's initial — see `ui/Icon.tsx`.
   */
  iconId?: string;
}

/**
 * One stat a node grants per rank.
 *
 * The stat *key*, not English — wording is the client's, the same division affix
 * `display_template` draws from the other side. A passive's numbers are a designer's
 * per-rank values rather than a rolled range, so there is no template to render
 * server-side.
 */
export interface PassiveStat {
  stat: string;
  perRank: number;
}

/** Why a node cannot take another point. The client owns the wording. */
export type PassiveBlocked = 'MAX_RANK' | 'LEVEL' | 'ATTRIBUTES' | 'NO_POINTS';

/**
 * One node as this player sees it.
 *
 * Unlike `SkillState`, **every** node arrives. Trimming to what is available would
 * hide the pool from a fresh character — precisely what made the skills panel open
 * empty until it was changed to render the catalogue.
 */
export interface PassiveState {
  passiveId: string;
  ranks: number;
  /** True when one more point could go in right now. */
  available: boolean;
  /** Why not, when `available` is false. */
  blocked?: PassiveBlocked;
}

/** One skill's static definition, mirrored from skills.yaml. */
export interface SkillInfo {
  skillId: string;
  name: string;
  category: string;
  tier: number;
  statGates: Attributes;
  /**
   * What must be in the main hand for this skill to be castable, from `weapon_types`.
   *
   * **Absent means any weapon**, which is what 97 of the hundred skills are; the three
   * that name one all describe firing an arrow, and before the field existed they fired
   * it out of a mace. Bare hands satisfy nothing — a requirement is a requirement.
   *
   * Sent rather than derived, for the reason every static skill number is: the list
   * lives in a YAML the client has no copy of. The panel renders it so the requirement
   * is visible *before* a skill point is spent; the server re-checks it on every cast,
   * because an action bar keeps its skills when the bow comes off.
   */
  weaponTypes?: WeaponClass[];
  scalingType: 'WEAPON' | 'ATTRIBUTE' | 'HYBRID' | 'PASSIVE' | 'UTILITY';
  staminaCost: number;
  manaCost: number;
  cooldownSec: number;
  /**
   * How long the server holds the caster before the outcome lands, from
   * `cast_time_sec`. Absent means instant, which is what 85 of the hundred skills are.
   *
   * Read twice: `castAnimSeconds` stretches the cast animation to it, so the body's
   * commit frame lands with the damage, and the action bar's tooltip says what a slot
   * commits the player to before they press it. Neither is computable from anything
   * else the client holds.
   */
  castTimeSec?: number;
  /**
   * How far this skill can be levelled: the file's `max_skill_rank` for a skill with
   * curves, and **1 for one without**.
   *
   * A cap of 1 is the honest answer rather than a missing feature. Fourteen entries —
   * the thirteen `PASSIVE` skills and `int_08` Mana Shield — have no number the
   * simulation reads, so a rank would move nothing and the panel says so instead of
   * offering a button that always refuses.
   *
   * Optional for the same reason `castTimeSec` is: the WELCOME decode passes the
   * skill array through whole rather than field by field, so an older server simply
   * omits it. Readers default to 1 — the conservative answer, since it offers no
   * button rather than one the server would refuse.
   */
  maxRank?: number;
  /**
   * Every levelled field's value at every rank, resolved server-side.
   *
   * **Sent rather than derived**, the rule an item's `sellValue` and the belt's
   * `healPerCharge` follow: the curve shapes live in a YAML this client has no copy
   * of, and the panel has to show what the next rank buys before the player commits.
   * Evaluating the curves here would be a second answer to a question the simulation
   * has already settled.
   *
   * Absent for a skill that cannot be levelled. `values[0]` is rank 1 and equals the
   * catalogue's authored number exactly.
   */
  ladder?: SkillLadder[];
  description: string;
  iconId?: string;
  /**
   * The skill's `visual_assets.animation_clip`, resolved against `CAST_ANIMATIONS` in
   * `game/models/poses.ts`.
   *
   * Typed loosely rather than as a union, for the reason `projectileShape` is: it is
   * content, and an id the client does not recognise must fall back rather than fail a
   * type guard. An unknown or absent id falls back to the scaling-type style — which is
   * what keeps the original property that adding a skill needs no animation work.
   */
  animationClip?: string;
  /** The skill's `color_palette`, as `#rrggbb`. Its bolt and impact are drawn in it. */
  colour?: string;
  colourAlt?: string;
  /**
   * The skill's `visual_assets.projectile_shape`, one of `PROJECTILE_SHAPES` in
   * `game/bolt.ts` — what the spell looks like in flight.
   *
   * Typed loosely rather than as the union on purpose: it is content, and a value the
   * client does not recognise has to fall back rather than fail a type guard. Absent for
   * every skill the catalogue says nothing about and for every skill that throws
   * nothing, and both resolve to `ORB` — the behaviour every spell had before shapes
   * existed, which is what keeps adding a skill free of visual work.
   *
   * Never an element. Typed damage does not exist, so a `fire`/`cold` field here would
   * promise a resistance nothing reads.
   */
  projectileShape?: string;
  /**
   * The skill's `audio_assets.sfx_cast` key, passed straight through (absent for "none"). The
   * client reads a sound family off its words — `castFamily` in `audio/cues.ts`.
   */
  sfxCast?: string;
}

export interface EntitySnapshot {
  id: string;
  /**
   * **Sent once, not every tick** — along with `maxHp`, `name`, `rank`, `traits`, `mesh`,
   * `armour` and `weapon`. None of them changes for an entity's life, and together they
   * were 51% of the frame. The server describes an entity the first tick it appears and
   * omits them after; `EntityDescriptions` in `net/rehydrate.ts` fills them back in before
   * anything downstream sees a snapshot, so every reader still gets a whole entity.
   *
   * Optional on the type because the wire genuinely omits them. Nothing below the
   * rehydrator should ever observe one absent.
   */
  kind: EntityKind;
  x: number;
  z: number;
  hp: number;
  maxHp: number;
  state: EntityState;
  facing: number;
  /**
   * Fraction of the attack cooldown still to run: 1 just after a swing, 0 when
   * the next one is ready. Drives the action bar's radial sweep.
   */
  cooldown: number;
  /**
   * Highest CMD_MOVE_TO sequence the server has applied for this entity's owner.
   * Only meaningful on the local player, where it gates reconciliation.
   */
  ackSeq: number;
  /**
   * Seconds of dodge cooldown still to run. Absent when ready, and for every mob.
   *
   * Here so the client can answer "can I roll?" **before** predicting one. Without it
   * the predictor rolled unconditionally, the server refused the press, and the 4-unit
   * error is past `RECONCILE_SNAP_DISTANCE` — so every press during the cooldown was a
   * visible snap. Seconds rather than a 0..1 fraction because readiness needs sub-tick
   * precision: the client decrements it locally between snapshots so it does not refuse a
   * roll that became legal 20 ms ago.
   */
  dodgeCooldown?: number;
  /**
   * True while a stun or a root is stopping the feet. Absent means free to walk.
   *
   * **A prediction input, and it ships before any stun art on purpose.** This predictor runs its
   * own copy of the walk ramp, so a hold it does not know about is not a missing effect, it is
   * drift: the server stops the body, the client walks on, and the gap crosses
   * `RECONCILE_SNAP_DISTANCE` (3.5) about three quarters of a second into a stun and then snaps.
   *
   * Stun and root only — `CanMove()` inverted, server-side. A **silence** is deliberately not
   * here: a silenced player walks normally, and a predictor told about one would stand still for
   * no reason at all.
   *
   * Meaningful only for the local player, like `ackSeq`. Mobs are interpolated, not predicted.
   */
  held?: boolean;
  /**
   * Movement speed multiplier from a slow. **Absent means ×1**, which is the ordinary case.
   *
   * Separate from `held` because a root is not a slow of zero: a slow is floored at ×0.3 server-side
   * so that it can never express a stop, and anything meant to hold a target outright is a root —
   * see ADR 0007. A *held* player still reports their slow scale here, so the two facts stay
   * independent and the client can tell "moving slowly" from "not moving".
   *
   * A present zero cannot occur, so `?? 1` is the correct read and not a guess.
   */
  moveScale?: number;

  /**
   * Seconds remaining on each kind of crowd control, for **display**. Phase 32.6, from
   * `doc/grind/design/STATUS-EFFECT-DISPLAY.md` §5.
   *
   * Sent for monsters as well as for the local player, which `held` and `moveScale` are not — that
   * is what lets a chilled monster be tinted, and it is the half `held`'s own comment was waiting
   * for.
   *
   * **Absent means none**, so a clean entity carries none of these. Four named fields rather than a
   * list because the server builds snapshots into reusable buffers and a per-entity array would
   * allocate every frame anything is debuffed — the same argument ADR 0007 made about the state
   * itself — and rather than a positional array because an index read as "silence" on this side is
   * a vocabulary mirrored by hand across the language boundary.
   *
   * **These do not replace `held`.** That is the server's answer to whether the feet work, and
   * deriving it here from `stunSec || rootSec` would put a copy of a server rule on this side to
   * drift, which is the `worldCentre`/`tileCentre` fault. Different consumers, different questions.
   */
  /**
   * Seconds left on a town portal's opening, for its bar. Phase 34.5.
   *
   * The local player's only, like `held` and `moveScale`: nobody else needs the number. What a
   * teammate needs is that the body is casting, and `state` already says `CASTING` for it.
   *
   * Absent when nothing is opening, which is almost every frame.
   */
  portalCastSec?: number;

  stunSec?: number;
  rootSec?: number;
  silenceSec?: number;
  slowSec?: number;
  /**
   * The monster's finished display name — "Vicious Bone Marksman".
   *
   * Assembled server-side from its archetype and elite prefix, and sent whole.
   * The client never maps an id to English; the wording lives beside the numbers,
   * exactly as an affix's `display` does. Absent for players, which have no
   * catalogue entry.
   */
  name?: string;
  /**
   * The classification tier, driving the nameplate's colour and frame and the
   * model's scale. Absent for players.
   */
  rank?: Rank;
  /**
   * An elite's parenthesised modifier tags — the "(Extra Fast)" in the spec's
   * *Vicious Frost Slasher (Extra Fast)*. Sent `omitempty`, so genuinely absent
   * for the ordinary mobs that make up most of a snapshot.
   */
  traits?: string[];
  /**
   * The archetype's `visuals.mesh_id`, deciding which model the client builds.
   *
   * Sent rather than derived from the name: the catalogue owns appearance, and
   * guessing "mummy" from a display name would break the moment an elite prefix
   * was added to it. Absent for players and for the anonymous practice dummy,
   * both of which fall back to a default model.
   */
  mesh?: string;
  /**
   * The visual class of this player's worn **chest** piece, or absent for a bare
   * chest and for every mob. Decides which body the client builds.
   *
   * Only the chest, per ARMOR-VISUALS.md §2. The other armour slots carry a
   * category in the catalogue and are cosmetically silent today; each is a further
   * layer rather than a change to this field.
   */
  armour?: ArmourClass;
  /**
   * The weapon family in this player's **main hand**, or absent for an empty hand and
   * for every mob. Decides which weapon the client builds, and — because a two-hander
   * blocks the off-hand — whether a shield is drawn on the off arm.
   *
   * The block is derived from this one field rather than sent as a second: storing a
   * copy of a two-hander in the off-hand would put one item in two slots, which is
   * exactly what the server refuses to do.
   */
  weapon?: WeaponClass;
  /**
   * The worn class of the other four armour slots, and what the off hand holds. Phase 41.
   *
   * Each is `omitempty` and static like `armour`: absent means the part the body always
   * had (the hood follows the chest, the sleeves are mail, the boots are leather, the off
   * hand carries the kite shield). The chest still decides the *body* and its collider;
   * these dress the head, the forearms, the thighs and the feet.
   */
  head?: ArmourClass;
  hands?: ArmourClass;
  legs?: ArmourClass;
  feet?: ArmourClass;
  offhand?: OffHandClass;
}

/**
 * Difficulty tiers, from the classification table in
 * `doc/grind/design/MOB-TAXONOMY-AFFIXES.md` §3.
 *
 * All five exist on the wire; the server only places NORMAL and ELITE today. The
 * client handles all of them so nothing here changes when the others arrive.
 */
export type Rank = 'NORMAL' | 'MAGIC' | 'ELITE' | 'UNIQUE' | 'BOSS';

/**
 * Whether an entity is a pack champion.
 *
 * Derived from `rank` rather than read from a flag of its own — the server sends
 * one field so the two can never disagree about the same monster.
 */
export function isElite(entity: Pick<EntitySnapshot, 'rank'>): boolean {
  return entity.rank === 'ELITE';
}

/**
 * Why the server refused a cast. A key, not a sentence — the wording is the
 * HUD's, like every other piece of English on screen.
 */
export type RefusalReason =
  | 'EMPTY_SLOT'
  | 'NOT_LEARNED'
  | 'GATE'
  | 'COOLDOWN'
  | 'RESOURCES'
  | 'NO_TARGET'
  | 'OUT_OF_RANGE'
  | 'PASSIVE'
  // A skill cast without the weapon it names. Its own key rather than `GATE`: a stat
  // gate is fixed by levelling and this is fixed by holding something else, so a player
  // told "GATE" while standing at the stat minimum has been told the wrong thing to do.
  | 'WRONG_WEAPON'
  // A second cast inside a cast window. Refused rather than queued: a queue is a
  // system nobody designed, and one that spent stamina on a skill the player had
  // stopped wanting would be worse than being told no.
  | 'CASTING'
  // A windowed cast attempted while walking or mid-roll. **This refusal is what lets
  // the cast window need no prediction at all** — the server never stops a walk the
  // predictor is already running, so the two halves cannot disagree about where the
  // body is. Instant casts still go off mid-stride.
  | 'MOVING'
  // Stunned or silenced. Phase 32. One key for both, because from the player's side
  // they are the same event — the character will not cast and there is nothing to do
  // but wait — and it is not `CASTING` or `MOVING`, which both describe something the
  // player is doing and could stop doing.
  | 'CONTROLLED'
  // Belt refusals, on EVENT_POTION_REFUSED. `COOLDOWN` is shared with the cast
  // reasons above: it means the same thing and reads the same way.
  | 'NO_CHARGES'
  | 'FULL_HEALTH'
  | 'DEAD'
  // Sale refusals, on EVENT_SELL_REFUSED.
  | 'NOT_IN_TOWN'
  | 'NOTHING_SOLD'
  // Workshop refusals, on EVENT_STATION_REFUSED. `NOT_IN_TOWN`, `DEAD` and
  // `OUT_OF_RANGE` above are all reused: each means here exactly what it means
  // everywhere else, and a second key for "too far" would be a second wording for one
  // idea. Only the station that is not in the layout is genuinely new.
  | 'UNKNOWN_STATION'
  // Gate refusals, on EVENT_GATE_REFUSED. `OUT_OF_RANGE` and `DEAD` above are reused —
  // being *at* a gate is still the server's measurement, and a corpse does not step
  // through one. Only "there is no gate to take" is new: a portal closes the moment
  // somebody else uses it, so a stale client can provoke it as easily as a modified one.
  | 'NO_GATE'
  // Container refusals, on EVENT_ITEM_MOVE_REFUSED. `NOT_IN_TOWN` and `DEAD` above are
  // reused — the vault is a place in town for the same reason scrapping is, and the
  // sentence is the same. `NO_SPACE` covers both "the container is out of cells" and
  // "the cells it has left are the wrong shape", because the HUD's answer is identical
  // and the difference is visible in the panel the player is looking at.
  | 'NO_SPACE'
  | 'BAD_CELL'
  | 'UNKNOWN_ITEM'
  | 'UNKNOWN_CONTAINER'
  // A move from the vault to the vault, which is not a gesture: the vault is a list and
  // has no cells to arrange. Only a client that thinks otherwise can provoke it.
  | 'NO_ARRANGEMENT'
  // A `CMD_DROP_ITEM` for a padlocked item. A lock guards against bulk actions and
  // against a misclick, and dropping is both — so it is refused outright rather than
  // silently skipped the way a bulk scrap skips one.
  | 'LOCKED'
  // Merchant refusals, on EVENT_BUY_REFUSED. `NOT_IN_TOWN`, `DEAD` and `NO_SPACE` above
  // are reused. `NO_OFFER` is a stale offer id — reachable from an ordinary client whose
  // panel is a few seconds older than the last reroll — and `NO_GOLD` is the one a player
  // provokes on purpose.
  | 'NO_OFFER'
  | 'NO_GOLD'
  // Skill-rank refusals, on EVENT_SKILL_LEVEL_REFUSED and on `SkillState.blocked`.
  // `NOT_LEARNED` and `GATE` above are reused, because those two conditions are
  // genuinely the same question; only these two are new. Both strings also match a
  // `PassiveBlocked` key — a passive node and a skill rank are the same purchase, so
  // two names for one answer would be the drift rather than the agreement.
  | 'MAX_RANK'
  | 'NO_POINTS'
  // Dodge refusals, on EVENT_DODGE_REFUSED. `COOLDOWN` and `RESOURCES` are shared with
  // the cast vocabulary — the same key, a different sentence. See `dodgeRefusalText`.
  | 'NO_DIRECTION'
  // Developer-command refusals, on EVENT_TEST_REFUSED. Worded in `TestPanel.tsx`
  // rather than in `ui/actionbar.ts`, because the panel that made the request is what
  // shows the answer — the other three share the action bar's corner of the screen.
  | 'NOT_PERMITTED'
  | 'UNKNOWN_BASE'
  | 'ITEM_LEVEL'
  | 'RARITY'
  | 'BAG_FULL'
  | 'UNREACHABLE'
  | 'LEVEL'
  | 'AMOUNT';

export interface GameEvent {
  type: string;
  entityId: string;
  /** Only meaningful for EVENT_ENTITY_DAMAGED. */
  sourceId?: string;
  /** Only meaningful for EVENT_ENTITY_DAMAGED. */
  amount?: number;
  /**
   * How much of a blow the Mana Shield paid out of mana, on EVENT_ENTITY_DAMAGED.
   *
   * `amount` keeps the meaning it has always had — the **health** the target lost — so a
   * shielded hit does not draw a damage number the player did not take. This is the other
   * half: the mana orb drops on the same frame, and without this there is no way to say
   * that is why. Absent on every unshielded hit, which is nearly all of them.
   */
  absorbed?: number;
  /**
   * A critical strike, on EVENT_ENTITY_DAMAGED.
   *
   * **The server rolled it**, against a seeded source inside `Step`, so it is
   * reproducible and a client cannot influence it. The client's only job is to draw the
   * number differently. Absent on an ordinary hit, which is most of them.
   */
  crit?: boolean;
  /**
   * Which rule stopped the action, on EVENT_SKILL_REFUSED and
   * EVENT_POTION_REFUSED. For a refused cast `amount` is the action-bar slot the
   * player pressed; a refused draught has no slot.
   */
  reason?: RefusalReason;
  /**
   * How many items a batch actually consumed, on EVENT_ITEMS_SOLD.
   *
   * Separate from `amount`, which is the scrap paid. A batch is partial by design, so
   * "you asked for twelve" and "nine were taken" are different facts.
   */
  count?: number;
  /**
   * Which workshop a refusal was about, on EVENT_STATION_REFUSED.
   *
   * Its own field rather than "whichever panel is open": the answer can arrive after
   * the player has switched tabs, and closing the wrong shop is worse than the
   * refusal it is reporting.
   */
  station?: StationKind;
  /**
   * Which container a refused placement was headed for, on EVENT_ITEM_MOVE_REFUSED.
   *
   * Its own field for the reason `station` is: two panels can be open side by side, and
   * "no space" means different things in each — a full bag ends a dive, a full vault
   * ends a season.
   */
  container?: ContainerId;
  /**
   * Where a cast went, for EVENT_SKILL_CAST — the target's position, or the aim
   * point when it hit nothing.
   *
   * Needed because the damage is already applied by the time this arrives, so the
   * projectile is purely a visual; and a spectator has no other way to know where
   * another player's bolt was headed.
   */
  aimX?: number;
  aimZ?: number;
  /**
   * A displacement the server has just begun, on `EVENT_SKILL_CAST_STARTED`.
   *
   * The client predicts toward `dashX`/`dashZ` over `dashSec` rather than computing either: the
   * distance is catalogue content and the duration is the character's own attack-speed-scaled
   * wind-up for two of the eight dashes, so working it out here would be a second answer to a
   * question the server has answered. Absent on every other event and every non-dash skill.
   */
  dashX?: number;
  dashZ?: number;
  dashSec?: number;
  /** How wide an `EVENT_AREA_STRIKE` reached, in world units. Absent on everything else. */
  radius?: number;
  /**
   * The area's geometry, mirroring `config.AoeShapes` in Go.
   *
   * Absent means a circle at the impact point — a monster's slam, and every player skill
   * before shapes existed. Sent because the client cannot infer it: one radius means a ring
   * on the floor for a stomp, a wedge for a cone and a corridor for a fissure, and drawing
   * the wrong one is drawing a lie about where the damage went.
   */
  shape?: AoeShape;
  /** A FRONTAL_CONE's full width in degrees. 90 means 45 either side of the facing. */
  angle?: number;
  /** The caster's bearing in radians, for the shapes measured along it. */
  facing?: number;
  /**
   * What a hit was made of, on EVENT_ENTITY_DAMAGED and EVENT_HIT_AVOIDED.
   *
   * **Absent means `PHYSICAL`**, the same convention `crit` uses for "no": most hits
   * in the game are physical, and events are the one part of a tick that scales with
   * how busy a fight is.
   *
   * It has to be sent because typed damage is otherwise invisible. The number that
   * arrives is *already* mitigated, so a player with fire resistance cannot tell a
   * reduced fire hit from an ordinary one, and has no way to learn which resistance
   * the zone is asking them to gear for. The client colours the number; it never
   * decides it.
   */
  damageType?: string;
  /**
   * The hit came from a distance, on the same two events.
   *
   * Only the server knows — a monster's reach is catalogue data no client receives,
   * and a snapshot two ticks old cannot be measured against for it. The client draws
   * a projectile from the source to the target, so a fourteen-unit archer looks like
   * one instead of hitting from off screen with no explanation.
   */
  ranged?: boolean;
}

/**
 * What a hit is made of. One vocabulary across `config.DamageTypes`,
 * `combat.DamageType` and this file — `TestConfigAndCombatAgreeOnDamageTypes` in
 * `internal/game` holds the Go halves together, and `protocol.test.ts` holds this
 * one to the same list.
 *
 * Only `FIRE`, `COLD` and `LIGHTNING` have a resistance stat behind them. `VOID` is
 * unresisted by design: nothing in `config.StatKeys` grants void resistance, which
 * is the Void-Touched prefix's whole identity.
 */
export const DAMAGE_TYPES = ['PHYSICAL', 'FIRE', 'COLD', 'LIGHTNING', 'VOID'] as const;

export type DamageType = (typeof DAMAGE_TYPES)[number];

/** Whether a string is a member of the vocabulary. */
export function isDamageType(value: string): value is DamageType {
  return (DAMAGE_TYPES as readonly string[]).includes(value);
}

export interface SnapshotPayload {
  tick: number;
  entities: EntitySnapshot[];
  /**
   * Ids that left this instance's view since the last frame.
   *
   * **Absence stopped meaning removal.** `entities` carries only what *changed*, so a
   * monster standing still is simply not in the frame; removal is stated rather than
   * inferred. `SnapshotAssembler` is the only thing that should read this.
   */
  gone?: string[];
  ground: GroundItem[];
  /**
   * Open town portals — normally none, at most one today.
   *
   * `omitempty` on the server, so the field is absent for most of a session and
   * always absent in town; the decoder defaults it to an empty array rather than
   * making every reader guard it.
   */
  portals: TownPortal[];
  events: GameEvent[];
  /**
   * The ids of the zone's fixtures that no longer stand, **in full** — but only on a frame where
   * one fell, and once a second besides. Absent means *no news*, never *everything stands*: the
   * store keeps the last list until the zone changes.
   */
  down?: number[];
}

/**
 * One side of the town portal: a place standing on the ground that takes a living
 * player through when they walk into it.
 *
 * Not an entity — no health, no state, no facing, and nothing can target it — so
 * it rides its own array. It is in the snapshot rather than the ZONE frame because
 * it appears *during* a zone, unlike the entry and the exit.
 *
 * There is at most one at a time, because it is one gate with two sides: `TOWN` in
 * a combat zone, going home; `RETURN` in town, going back into the run that was
 * suspended when the first one was used.
 */
export interface TownPortal {
  id: string;
  x: number;
  z: number;
  /**
   * Which way this gate leads. **Absent means `TOWN`** — the server omits the
   * common case, the convention `armour`, `traits` and `crit` already follow.
   */
  kind?: PortalKind;
  /**
   * The enemy level waiting on the other side, sent only for a `RETURN` gate.
   *
   * The client cannot work it out: in town the ZONE frame describes the hub and
   * says nothing about the suspended run, so this is the only way a player learns
   * what depth they are stepping back into before they commit to it. Absent for a
   * `TOWN` gate, where the player is already standing in the answer.
   */
  level?: number;
  /**
   * Who may walk through this gate — a `RETURN` gate only, absent on a player's own portal.
   *
   * **On the wire because the snapshot is per *instance*, not per player.** Town is one shared
   * instance encoded once and broadcast to everybody in it, so the server cannot cheaply hand
   * each player a different gate list; the alternative is a frame per player in the busiest
   * instance in the game. So the filtering is the client's — see `store.portals`.
   *
   * Presentation, never a rule: the server refuses a gate the asker does not own regardless, so
   * a modified client that drew all of them still could not use one.
   */
  owners?: string[];
}

/** Which side of the gate a portal is. See `TownPortal.kind`. */
export type PortalKind = 'TOWN' | 'RETURN';

/**
 * Whether a portal leads back into a suspended run rather than home.
 *
 * A function rather than a comparison at each call site, because the default is
 * *absence*: `kind === 'TOWN'` is false for the commonest gate in the game, and
 * that is exactly the kind of inversion that ships a mislabelled marker.
 */
export function isReturnGate(portal: TownPortal): boolean {
  return portal.kind === 'RETURN';
}

/** Biome ids, per doc/grind/architecture/ZONE-GENERATION.md. Visual only. */
export type BiomeId =
  | 'BIOME_CRYPTS'
  | 'BIOME_CALDERA'
  | 'BIOME_ABYSS'
  | 'BIOME_WASTES'
  | 'BIOME_MARSH'
  | 'BIOME_THICKET'
  | 'BIOME_CAVERNS'
  | 'BIOME_HUB';

/** HUB is town; COMBAT is a generated zone. Only town shows the GRIND portal. */
export type ZoneKind = 'HUB' | 'COMBAT';

/**
 * Where a cast's area sits, mirroring `config.AoeShapes` in Go.
 *
 * **A hand-copied vocabulary, so it is walked in both directions by a test.** A case for a
 * shape the server never sends is invisible — it simply never matches — and a shape the
 * server sends that the client has no case for falls through to whatever the default draws.
 * That is the `slotsFor`/`WEAPON` fault exactly.
 */
export const AOE_SHAPES = [
  'NONE',
  'TARGET_RADIAL',
  'SELF_RADIAL',
  'FRONTAL_CONE',
  'LINE',
  'SELF',
] as const;
export type AoeShape = (typeof AOE_SHAPES)[number];

/** Item rarities, per doc/grind/design/ECONOMY-LOOT.md. Four tiers. */
export type ItemRarity = 'NORMAL' | 'MAGIC' | 'RARE' | 'UNIQUE';

/** Ascending worth, for "this rarity and better" filters. */
export const RARITY_ORDER: readonly ItemRarity[] = ['NORMAL', 'MAGIC', 'RARE', 'UNIQUE'];

/** One item lying on the floor. */
export interface GroundItem {
  id: string;
  x: number;
  z: number;
  rarity: ItemRarity;
  name: string;
  glyph: string;
  /** Coins in this pile, for a drop that is money. Absent on a rolled item. */
  gold?: number;
  /**
   * The only player who may take this, or absent for anyone's.
   *
   * A monster rolls a separate drop for each player who was in the fight, so what is
   * yours is yours and nobody can beat you to it. Everyone still *receives* every drop —
   * there is one snapshot per tick, encoded once and broadcast — so the client's job is
   * to say which are which; the server refuses the rest either way.
   *
   * **Absent means anyone's**, and that is the case that matters most: an item another
   * player dropped by hand is a gift, and dropping something at a friend's feet is the
   * only way to hand anything over in this game.
   */
  ownerId?: string;
}

export interface ItemAffix {
  /** The catalogue's stat key, e.g. "physical_damage_pct". */
  stat: string;
  slot: 'PREFIX' | 'SUFFIX';
  /** 10 (weakest) to 1 (best); 0 means a unique's fixed roll, which has no tier. */
  tier: number;
  value: number;
  /** The affix's flavour name from the catalogue — "Heavy / Sharp". */
  name: string;
  /**
   * The finished line to show, rendered server-side from the catalogue's
   * `display_template`. The client never has to know what a stat key means, and
   * the wording lives beside the numbers in the YAML.
   */
  display: string;
}

export interface InventoryItem {
  uuid: string;
  name: string;
  baseId: string;
  slot: string;
  glyph: string;
  /**
   * Names this item's 128px art, from `visual_assets.icon_id` on its catalogue base.
   *
   * Absent, or naming a file that has not been drawn yet, means the `glyph` is what
   * gets drawn — which is what lets art land one sheet at a time without a
   * half-illustrated inventory in between. See `ui/icons.ts`.
   */
  iconId?: string;
  rarity: ItemRarity;
  itemLevel: number;
  affixes: ItemAffix[];
  /**
   * What a weapon does on its own: the product, the band, and the rate.
   *
   * **Absent on anything that is not a weapon**, so `undefined` is the normal answer for a ring and
   * the caller draws nothing rather than a zero.
   *
   * Computed server-side by `World.WeaponNumbers`, because the client does not have the inputs — a
   * weapon's band and rate are catalogue data — which is why the bag could never show a weapon's
   * damage before. `dps` excludes `physical_damage_pct`: in the additive model the simulation runs
   * today that stat scales attribute damage rather than the weapon band, so folding it in would
   * state a relationship the server does not have. It becomes correct with roadmap Phase 21.
   */
  /**
   * What wearing this instead would do to effective health — the upgrade signal.
   *
   * Replaces item level, which the arrow used to compare with its own comment admitting the
   * problem: *"the simulation does not read most affixes yet, so a 'better' computed from them
   * would be a confident lie"*. Computed server-side by re-deriving the whole loadout with the item
   * in its socket, so armour, resistances and a `health_flat` roll interact correctly — and an item
   * that trades armour for health gets the right **sign**, which is the case an armour-points
   * approximation would get wrong.
   *
   * Carried items only, and absent when it changes nothing.
   */
  toughnessDelta?: number;
  dps?: number;
  damageMin?: number;
  damageMax?: number;
  attacksPerSec?: number;
  isLocked: boolean;
  /**
   * What this item would pay if scrapped. **Carried items only** — an equipped item
   * omits it, because it cannot be scrapped and quoting a price would be a
   * plausible-looking lie.
   *
   * Sent rather than derived, for the reason `Belt.healPerCharge` is: the prices and
   * the level formula live in `configs/economy.yaml`, the client has no copy, and a
   * bulk-scrap UI has to total a selection before the player commits. Never compute
   * it client-side.
   */
  sellValue?: number;
  /** Set only on uniques: the named mechanic. Not simulated yet. */
  power?: string;
  /**
   * The item's attribute gate, sent for **carried** items as well as worn ones.
   *
   * Without it the client could not say whether a drop was wearable or why — which
   * made the items a player is working toward the only ones whose requirements were
   * invisible.
   */
  requires: StatRequirements;
  /** The item's armour contribution, for comparing against what is worn. */
  armour?: number;

  /**
   * Where the item sits in its container, and how many cells it covers.
   *
   * **Sent, never computed.** The client has no copy of `grid_footprints` and must not
   * get one: a size derived here would be a second source of truth for the number that
   * decides whether two items overlap, and the two disagreeing is a grid that draws a
   * legal placement the server refuses — the `worldCentre`/`tileCentre` failure, in a
   * panel.
   *
   * All four are optional because they are `omitempty` on the wire, and that is a real
   * trap rather than a saving: `col: 0, row: 0` is the top-left square and the
   * commonest placement there is, so an **absent key means zero** and never "unknown".
   * An equipped item legitimately has none of the four — a worn item has a slot, not a
   * cell, and occupies no area.
   */
  col?: number;
  row?: number;
  width?: number;
  height?: number;
}

/**
 * One container: its dimensions, and what is placed in it.
 *
 * The dimensions ride every frame rather than WELCOME, and that is deliberate despite
 * them never changing today: a bag that grows is the obvious first upgrade a vendor
 * sells, and a client caching the size from a boot frame would draw the old grid until
 * the player reconnected.
 */
export interface ContainerPayload {
  cols: number;
  rows: number;
  items: InventoryItem[];
}

/**
 * The vault: a capacity, and items in the order they were stored.
 *
 * **Deliberately not a `ContainerPayload`.** The vault has no cells to place anything
 * in — it is a thousand-item list this client sorts and filters — so it carries a count
 * where the bag carries a rectangle. Sharing the type would mean reading `cols` and
 * `rows` that no gesture could act on.
 *
 * Its items carry `width`/`height` and never `col`/`row`: exactly what a stored item
 * has, which is a size and nowhere to be. The panel draws the shape so a player can see
 * what a withdrawal will cost their bag before they ask for it.
 */
export interface VaultPayload {
  /** How many items fit — not an area. */
  capacity: number;
  items: InventoryItem[];
}

/**
 * The two containers, mirroring `protocol.Containers` on the server.
 *
 * **Two, and the paper doll is not one of them.** A worn item has a slot, not a cell,
 * and occupies no area — which is why equipping is still `CMD_EQUIP_ITEM` rather than a
 * move into a third container.
 */
export const CONTAINER_BAG = 'BAG';
export const CONTAINER_VAULT = 'VAULT';

export const CONTAINERS = [CONTAINER_BAG, CONTAINER_VAULT] as const;

export type ContainerId = (typeof CONTAINERS)[number];

/**
 * The eleven paper-doll slots, in the order the sheet reads them.
 *
 * Mirrors item.DollSlots on the server, including the order — the sheet lays
 * them out in sequence and the server's equipment map is keyed by these strings.
 *
 * (EQUIPMENT-SLOTS.md calls this a "10-Slot Matrix" but its own table,
 * paper doll and JSON schema all list eleven. Eleven is what both sides use.)
 */
export const DOLL_SLOTS = [
  'HEAD',
  'AMULET',
  'CHEST',
  'GLOVES',
  'BELT',
  'LEGS',
  'BOOTS',
  'RING_1',
  'RING_2',
  'MAIN_HAND',
  'OFF_HAND',
] as const;

export type DollSlot = (typeof DOLL_SLOTS)[number];

export interface StatRequirements {
  str: number;
  dex: number;
  int: number;
  end: number;
}

export interface EquippedItem {
  item: InventoryItem;
  handing?: string;
  requires: StatRequirements;
  armour?: number;
  /**
   * The off-hand is blocked by a two-handed weapon rather than holding anything
   * of its own. The sheet greys it instead of offering it as a target.
   */
  isVirtualLink?: boolean;
}

export interface Attributes {
  str: number;
  dex: number;
  int: number;
  end: number;
}

export interface AttributeBlock {
  base: Attributes;
  gear: Attributes;
  total: Attributes;
  /** Unspent level-up points. Always 0 — there is no XP or level system yet. */
  unspent: number;
}

/**
 * Everything computed from attributes and equipment.
 *
 * The first four are consumed by the simulation. The rest are real sums of real
 * gear that nothing reads yet, because no design document says how they should
 * apply — the sheet labels them rather than implying an effect.
 */
export interface DerivedStats {
  maxHealth: number;
  maxStamina: number;
  maxMana: number;
  staminaRegen: number;
  manaRegen: number;
  attackDamage: number;
  attackCooldown: number;
  moveSpeed: number;
  /**
   * How far the basic attack reaches, in world units.
   *
   * **On the derived block rather than on `WELCOME`, because it varies with gear.** It
   * was a flat 2.0 for everybody, which made a bow a sword that fired an arrow animation
   * — the whole difference between a ranged weapon and a melee one is the distance you
   * may stand at. `attack_range` is a weapon's own stat and replaces the unarmed
   * baseline, exactly as `attack_speed` replaces the unarmed cooldown.
   */
  attackRange: number;

  physicalAttackPower: number;
  magicAttackPower: number;
  attackRating: number;
  spellCritChancePct: number;
  spellDamagePct: number;
  evasion: number;
  blockChancePct: number;
  healthRegen: number;
  fireResistPct: number;
  coldResistPct: number;
  lightningResistPct: number;
  potionEfficiencyPct: number;
  potionCapacity: number;
  /**
   * Armour is a **score** fed to a hyperbolic curve, not a percentage —
   * `physicalMitigationPct` is what it actually buys, computed server-side so the
   * client never reimplements the formula.
   */
  armour: number;
  physicalMitigationPct: number;
  /**
   * How much raw **physical** damage this character absorbs before dying — health divided by what
   * armour does not stop.
   *
   * The number that makes armour legible: "9% mitigation" reads as worthless and "+10% effective
   * health" does not, and it is what answers *is +100 HP better than +50 armour*, which no stat line
   * on the sheet can answer on its own.
   *
   * **Computed server-side, and it must stay that way.** It comes from `combat.EffectiveHealth`,
   * the same function the simulation mitigates with — a second effective-health formula here would
   * let the sheet disagree with the server about how much a hit takes off, and a player would gear
   * against the disagreement. Physical, because mitigation is per damage type and there is no single
   * true scalar; the sheet labels it as such. Counts health, armour and resistances, and **not**
   * energy shield, evasion or block, because the simulation reads none of those.
   */
  toughness: number;
  critChancePct: number;
  critDamagePct: number;
  /**
   * The dodge roll's three scaling numbers. Endurance shortens the first two,
   * dexterity lengthens the third.
   *
   * The roll's distance and duration are **not** here — they do not scale, so they ride
   * WELCOME. These three are read by the simulation, not merely displayed.
   */
  dodgeCooldown: number;
  dodgeStaminaCost: number;
  dodgeIFrames: number;
}

/** Level, experience and unspent points. */
export interface Progression {
  level: number;
  xp: number;
  xpIntoLevel: number;
  xpForNext: number;
  attributePoints: number;
  skillPoints: number;
  passivePoints: number;
}

/**
 * One levelled field's value at every rank.
 *
 * The stat *key*, never English — a path into the skill's own YAML entry, one of
 * `config.SkillStatKeys` in Go. Wording is the client's, the same division
 * `PassiveStat` draws.
 */
export interface SkillLadder {
  stat: string;
  /** Index 0 is rank 1. */
  values: number[];
}

/** One skill as this player sees it. The definitions come from WELCOME. */
export interface SkillState {
  skillId: string;
  learned: boolean;
  /**
   * How many points are in this skill: 0 unlearned, 1 once learned, up to the
   * catalogue's cap. Absent means 0.
   *
   * `learned === (rank > 0)` on the server by construction — learning *is* buying
   * rank 1 — and both are sent because the client asks both questions.
   */
  rank?: number;
  /** False when the attribute gate is no longer met — disabled, not unlearned. */
  usable: boolean;
  /** Seconds remaining, absent when ready. */
  cooldown?: number;
  /**
   * Why another point cannot go in right now, absent when one can.
   *
   * The **same** key the server would refuse the command with, so the greyed button
   * and the answer to a click on it say one thing.
   */
  blocked?: RefusalReason;
}

/** Current pools; their maxima are in DerivedStats. */
export interface Pools {
  stamina: number;
  mana: number;
  /**
   * Seconds until stamina starts recovering; absent or zero when it already is.
   *
   * The client integrates regeneration **locally** — both pools change every tick and the
   * CHARACTER frame is far too heavy to send that often, so a *spend* is pushed and *recovery*
   * is predicted from the rates on `DerivedStats`. This is the one part the client cannot
   * derive: recovery waits out a delay after the last expenditure, and neither the delay nor
   * the idle timer is anything the client holds. Mana has no such rule and no such field.
   */
  staminaResumesIn?: number;
  /**
   * The fraction of a blow the Mana Shield toggle is currently paying out of mana, in
   * percentage points. Absent or zero means the toggle is **off**, which is the common case.
   *
   * The percentage rather than a bool, because the client holds no copy of the catalogue and
   * *"absorbing 60%"* is the thing worth telling a player — the number can only come from
   * the server.
   */
  manaShieldPct?: number;
}

/**
 * The potion belt.
 *
 * `capacity` and `healPerCharge` are sent rather than derived, because both depend
 * on numbers that live in the server's `game.Config` — the baseline capacity every
 * character has, and the heal fraction. Deriving them here would be a second source
 * of truth for numbers the server owns.
 */
export interface Belt {
  charges: number;
  capacity: number;
  /** Seconds left on the shared belt cooldown, absent when ready. */
  cooldown?: number;
  /** What one draught would restore right now, including belt efficiency. */
  healPerCharge: number;
}

export interface CharacterPayload {
  attributes: AttributeBlock;
  derived: DerivedStats;
  progression: Progression;
  pools: Pools;
  belt: Belt;
  /** Only skills learned or currently learnable. */
  skills: SkillState[];
  /** Every node in the pool, with this player's ranks on it. */
  passives: PassiveState[];
  /** The five action-bar assignments, by skill id; empty means free. */
  slotted: string[];
  /** Keyed by doll slot. Every slot is present; an empty one is null. */
  equipment: Partial<Record<DollSlot, EquippedItem | null>>;
}

export interface InventoryPayload {
  /**
   * The bag is carried. The vault is a place in town — the server refuses every move
   * touching it outside the hub — but its *contents* arrive wherever the player is,
   * because knowing what you own is not the privilege the town gates; walking to it is.
   *
   * A flat top-level `capacity`/`items` pair is **gone**: one list cannot say which
   * container an item is in. The vault has a capacity of its own again, because a list
   * is exactly what it turned out to want to be — see `VaultPayload`.
   */
  bag: ContainerPayload;
  vault: VaultPayload;
  /**
   * The player's scrap balance.
   *
   * It rides the INVENTORY frame rather than the snapshot because the bag and the
   * balance always change together — a second frame would be a second thing to keep in
   * step — and because a snapshot is marshalled once for everybody while a balance is
   * private. **Nothing spends it yet**, so the HUD must show it without implying
   * prices exist.
   */
  /**
   * What a merchant has paid, and what the shop charges against.
   *
   * A second balance rather than one number, because scrapping and selling are different
   * verbs paying different currencies: scrapping destroys an item for materials, selling
   * gives it away for coin.
   */
  gold: number;
  /**
   * What the stash is holding.
   *
   * Apart from `gold` because that is the point of a stash: what you are carrying is what
   * you drop when you die, and what is banked survives.
   */
  stashedGold: number;
  /**
   * This player's shop. It rides this frame for the reason the balances do — it is
   * private, it changes rarely, and it always changes *with* one of them: a purchase moves
   * the gold and the shelf in the same instant.
   */
  vendor: VendorPayload;
}

export interface VendorPayload {
  /**
   * Seconds until the stock is thrown away and rolled again.
   *
   * A countdown rather than a deadline, because the client has no idea what time the
   * server thinks it is — and a countdown is what the panel actually draws.
   */
  rerollInSec: number;
  offers: VendorOffer[];
}

export interface VendorOffer {
  id: string;
  /**
   * Stamped when the shop was rolled, so what the player is looking at and what they will
   * be charged are the same number. Never computed here.
   */
  price: number;
  item: InventoryItem;
}

/** What occupies one cell of the zone grid. */
export type TileKind =
  | 'VOID'
  | 'ENTRY'
  | 'CORRIDOR'
  | 'ROOM'
  | 'EXIT_GATE'
  | 'PORTAL'
  /** A structure standing on the map — a house in town, a ruin in a crypt. */
  | 'BUILDING'
  /** The town's curtain wall. Taller than a building. */
  | 'RAMPART'
  /** A walkable gap in a rampart. */
  | 'GATE'
  /**
   * Terrain standing in a room: a boulder, a dead trunk, a stalagmite, a fallen
   * column. Unwalkable **and** solid, so it draws a block on its own tile and no
   * wall goes on the boundary facing it — a BUILDING's rule applied to nature.
   *
   * Not a sight blocker: nothing in the simulation reads line of sight.
   */
  | 'OBSTACLE'
  /**
   * Standing liquid — bog water, lava, void mist. Unwalkable and **not** solid,
   * so it draws neither a wall nor a block: it paints its own recessed surface in
   * the biome's liquid colour. Crossing it is impossible rather than costly; there
   * is no hazard system, so a pool that hurt would be a stat nothing computes.
   */
  | 'POOL';

export interface ZoneTile {
  kind: TileKind;
  col: number;
  row: number;
}

export interface ZonePoint {
  x: number;
  z: number;
}

/**
 * The map to build, sent once between WELCOME and the first snapshot.
 *
 * `biome` decides only how the zone looks and `enemyLevel` only how hard it
 * hits — the decoupling the spec insists on. The client must never derive
 * difficulty from the biome or vice versa.
 */
export interface ZonePayload {
  seed: number;
  kind: ZoneKind;
  biome: BiomeId;
  enemyLevel: number;
  /**
   * How crowded the zone is: 1.0 is a baseline zone, 0 is the hub. The packs
   * themselves are deliberately not sent — this is the only advance warning of
   * what is in there.
   */
  density: number;
  width: number;
  height: number;
  tileSize: number;
  tiles: ZoneTile[];
  spawn: ZonePoint;
  exit: ZonePoint;
  /**
   * The town's workshops. Absent outside the hub — town is the only place with any.
   *
   * Serialised, unlike the zone's packs: a shopfront is not an ambush. The client needs
   * the positions to draw a sign on the street and to label the icons that teleport
   * there.
   */
  stations?: ZoneStation[];
  /**
   * The zone's doors and breakables (`design/LEVEL-FIXTURES.md`). Whether each still stands is
   * the snapshot's `down`. Empty in town.
   */
  fixtures?: ZoneFixture[];
}

/**
 * A door or a breakable. A door's segment is centred on `at`, a tile long, along X when `alongX`
 * and along Z otherwise; a breakable is a circle at `at`. Mirrors `zone.Fixture` in Go.
 */
export interface ZoneFixture {
  id: number;
  kind: 'DOOR' | 'BREAKABLE';
  at: ZonePoint;
  alongX?: boolean;
}

export type ServerFrame =
  | { type: typeof FRAME_WELCOME; payload: WelcomePayload }
  | { type: typeof FRAME_ZONE; payload: ZonePayload }
  | { type: typeof FRAME_INVENTORY; payload: InventoryPayload }
  | { type: typeof FRAME_CHARACTER; payload: CharacterPayload }
  | { type: typeof FRAME_PARTY; payload: PartyPayload }
  | { type: typeof FRAME_SOCIAL; payload: SocialPayload }
  | { type: typeof FRAME_CHAT; payload: ChatPayload }
  | { type: typeof FRAME_SNAPSHOT; payload: SnapshotPayload };

/**
 * Parses one inbound frame.
 *
 * Returns null for anything unrecognised — malformed JSON, a frame type this
 * client predates, a payload of the wrong shape. An older client talking to a
 * newer server should degrade, not crash.
 */
export function decodeFrame(raw: string): ServerFrame | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) return null;
  const frame = parsed as { type?: unknown; payload?: unknown };

  if (frame.type === FRAME_WELCOME) {
    const p = frame.payload as Partial<WelcomePayload> | undefined;
    if (!p || typeof p.playerId !== 'string') return null;
    return {
      type: FRAME_WELCOME,
      payload: {
        playerId: p.playerId,
        tickRate: typeof p.tickRate === 'number' ? p.tickRate : 30,
        worldSize: typeof p.worldSize === 'number' ? p.worldSize : 50,
        playerSpeed: typeof p.playerSpeed === 'number' ? p.playerSpeed : 5,
        // A *present* zero must survive as zero — it is how a server says the ramp is
        // off, and substituting 0.15 there would have the client ramp while the server
        // does not. Only an absent or non-numeric field falls back, and it falls back
        // to the shipped default rather than to zero so an older server produces a
        // slightly-behind prediction rather than a permanently-ahead one.
        walkRampTime: typeof p.walkRampTime === 'number' ? p.walkRampTime : 0.15,
        // Defaulted like every other field here, so an older server that does not
        // send them yields a roll that is short rather than one of length NaN — which
        // would put the predicted position at NaN and lose the player entirely.
        dodgeDistance: typeof p.dodgeDistance === 'number' ? p.dodgeDistance : 4,
        dodgeDuration: typeof p.dodgeDuration === 'number' ? p.dodgeDuration : 0.3,
        // Defaulted to the shipped values for the same reason the roll's are: an older
        // server that does not send them should give a chase that stops a little short
        // or a little long, never one that walks to NaN.
        playerAttackRange:
          typeof p.playerAttackRange === 'number' ? p.playerAttackRange : 2,
        stationRange: typeof p.stationRange === 'number' ? p.stationRange : 4,
        // Defaulted to the shipped numbers so a frame from an older server still walks
        // to a gate rather than stopping on the spot — the same defaulting stationRange
        // gets, and honest about being a fallback rather than the rule.
        portalEntryRange: typeof p.portalEntryRange === 'number' ? p.portalEntryRange : 2.5,
        exitRange: typeof p.exitRange === 'number' ? p.exitRange : 3.2,
        // 2.6 is the shipped value; an older server that does not send it gets the walk
        // it would have had anyway.
        pickupRange: typeof p.pickupRange === 'number' ? p.pickupRange : 2.6,
        skills: Array.isArray(p.skills) ? p.skills : [],
        attributePointsPerLevel:
          typeof p.attributePointsPerLevel === 'number' ? p.attributePointsPerLevel : 5,
        skillPointsPerLevel: typeof p.skillPointsPerLevel === 'number' ? p.skillPointsPerLevel : 1,
        passivePointsPerLevel:
          typeof p.passivePointsPerLevel === 'number' ? p.passivePointsPerLevel : 1,
        passives: Array.isArray(p.passives) ? p.passives : [],
        // Absent means off, and absent has to stay absent: the panel's whole existence
        // is keyed off this being undefined, so a `{}` substituted here would show a
        // developer panel that every button in was refused. An object with no
        // `commands` array is treated as off for the same reason.
        ...(p.testMode && Array.isArray(p.testMode.commands)
          ? { testMode: decodeTestMode(p.testMode) }
          : {}),
      },
    };
  }

  if (frame.type === FRAME_ZONE) {
    const p = frame.payload as Partial<ZonePayload> | undefined;
    // A zone without tiles is not renderable, so it is rejected outright rather
    // than producing an empty map the player would be trapped in.
    if (!p || !Array.isArray(p.tiles) || typeof p.biome !== 'string') return null;
    if (!p.width || !p.height || !p.tileSize) return null;

    return {
      type: FRAME_ZONE,
      payload: {
        seed: typeof p.seed === 'number' ? p.seed : 0,
        kind: (p.kind === 'HUB' ? 'HUB' : 'COMBAT') as ZoneKind,
        biome: p.biome as BiomeId,
        enemyLevel: typeof p.enemyLevel === 'number' ? p.enemyLevel : 1,
        density: typeof p.density === 'number' ? p.density : 0,
        width: p.width,
        height: p.height,
        tileSize: p.tileSize,
        tiles: p.tiles,
        spawn: p.spawn ?? { x: 0, z: 0 },
        exit: p.exit ?? { x: 0, z: 0 },
        stations: Array.isArray(p.stations) ? p.stations : [],
        fixtures: Array.isArray(p.fixtures) ? p.fixtures : [],
      },
    };
  }

  if (frame.type === FRAME_PARTY) {
    const p = (frame.payload ?? {}) as Partial<PartyPayload>;
    // **An empty payload is the "no party" answer, not a malformed frame.** The server
    // sends one when a party dissolves, and rejecting it would leave the HUD showing a
    // group that no longer exists.
    return {
      type: FRAME_PARTY,
      payload: {
        id: typeof p.id === 'string' ? p.id : undefined,
        leaderId: typeof p.leaderId === 'string' ? p.leaderId : undefined,
        members: Array.isArray(p.members) ? p.members : [],
      },
    };
  }

  if (frame.type === FRAME_SOCIAL) {
    const p = (frame.payload ?? {}) as Partial<SocialPayload>;
    return {
      type: FRAME_SOCIAL,
      payload: {
        online: Array.isArray(p.online) ? p.online : [],
        invite:
          p.invite && typeof p.invite.fromName === 'string' ? { fromName: p.invite.fromName } : undefined,
      },
    };
  }

  if (frame.type === FRAME_CHAT) {
    const p = frame.payload as Partial<ChatPayload> | undefined;
    if (!p || !Array.isArray(p.messages)) return null;
    // Each line is checked rather than the batch: one malformed message must not throw
    // away the others that arrived with it.
    const messages = p.messages.filter(
      (m): m is ChatMessage =>
        !!m && typeof m.text === 'string' && typeof m.fromName === 'string',
    );
    return { type: FRAME_CHAT, payload: { messages } };
  }

  if (frame.type === FRAME_INVENTORY) {
    const p = frame.payload as Partial<InventoryPayload> | undefined;
    // The bag is the required half: a frame without one says nothing about what the
    // player is carrying, and a panel drawn from it would be a lie. A frame without a
    // *vault* is survivable — an empty 0×0 grid renders as "nothing here" — but it is
    // still normalised rather than passed through undefined, because every reader
    // indexes `.items`.
    if (!p || !p.bag || !Array.isArray(p.bag.items)) return null;
    return {
      type: FRAME_INVENTORY,
      payload: {
        bag: normaliseContainer(p.bag),
        vault: normaliseVault(p.vault),
        gold: typeof p.gold === 'number' ? p.gold : 0,
        stashedGold: typeof p.stashedGold === 'number' ? p.stashedGold : 0,
        // An absent vendor is an empty shelf with nothing on the clock, which the panel
        // reads as "no stock" rather than as a shop about to reroll.
        vendor: {
          rerollInSec:
            typeof p.vendor?.rerollInSec === 'number' ? p.vendor.rerollInSec : 0,
          offers: Array.isArray(p.vendor?.offers) ? p.vendor.offers : [],
        },
      },
    };
  }

  if (frame.type === FRAME_CHARACTER) {
    const p = frame.payload as Partial<CharacterPayload> | undefined;
    // Attributes and derived stats are the whole point of the frame; without
    // them there is nothing to render and a half-built sheet is worse than none.
    if (!p || !p.attributes || !p.derived) return null;
    return {
      type: FRAME_CHARACTER,
      payload: {
        attributes: p.attributes,
        derived: p.derived,
        progression: p.progression ?? {
          level: 1,
          xp: 0,
          xpIntoLevel: 0,
          xpForNext: 0,
          attributePoints: 0,
          skillPoints: 0,
          passivePoints: 0,
        },
        pools: p.pools ?? { stamina: 0, mana: 0 },
        // A belt of zero charges rather than a plausible full one: a HUD widget
        // showing draughts the player may not have is a lie they cannot see through.
        belt: p.belt ?? { charges: 0, capacity: 0, healPerCharge: 0 },
        skills: p.skills ?? [],
        passives: p.passives ?? [],
        slotted: p.slotted ?? [],
        equipment: p.equipment ?? {},
      },
    };
  }

  if (frame.type === FRAME_SNAPSHOT) {
    const p = frame.payload as Partial<SnapshotPayload> | undefined;
    if (!p || !Array.isArray(p.entities)) return null;
    return {
      type: FRAME_SNAPSHOT,
      payload: {
        tick: typeof p.tick === 'number' ? p.tick : 0,
        entities: p.entities,
        // **The removals, and leaving this line out cost days.**
        //
        // This function does not pass the payload through, it *rebuilds* it field by field —
        // so a field that is not named here is silently deleted between the socket and the
        // store, however correct the server and the wire are. `gone` was added to the Go
        // struct, to `SnapshotPayload` above, and to every reader, and never to this list.
        //
        // The result was invisible from both ends. The server logged the ids it was removing,
        // the raw WebSocket text contained `"gone":["mob-147",…]`, and the store simply never
        // saw the field: monsters died and stayed, standing at the health they had, unkillable
        // because the server had long since forgotten them. Absence stopped meaning removal
        // when the delta landed, so a dropped `gone` is a permanent one.
        //
        // `omitempty` on the server, so absent is the ordinary case and means nothing left.
        gone: Array.isArray(p.gone) ? p.gone : [],
        ground: Array.isArray(p.ground) ? p.ground : [],
        // Absent means no portal is open, which is the usual case and always the
        // case in town.
        portals: Array.isArray(p.portals) ? p.portals : [],
        events: Array.isArray(p.events) ? p.events : [],
        // Named here or deleted here — see `gone` above. Absent is "no news", so it stays absent.
        ...(Array.isArray(p.down) ? { down: p.down } : {}),
      },
    };
  }

  return null;
}

/**
 * Fills in the developer panel's block, defaulting the two fields the panel would
 * otherwise have to guard at every use.
 *
 * `bases` is defaulted to empty rather than the frame being rejected: a panel with no
 * spawnable list is still a panel that can set a level and fill a belt, and refusing
 * the whole block would hide three working commands over one missing array.
 */
/**
 * Fills in a container the server may have omitted, or sent half of.
 *
 * A 0 × 0 grid with no items is the honest empty: it renders as "nothing here" and
 * every reader can index `.items` without a guard. The alternative — passing
 * `undefined` through — would put an optional-chain on every access in three panels
 * and a drag handler, and the one that got forgotten would be a runtime crash inside
 * a drag.
 */
export function normaliseContainer(p: Partial<ContainerPayload> | undefined): ContainerPayload {
  return {
    cols: typeof p?.cols === 'number' ? p.cols : 0,
    rows: typeof p?.rows === 'number' ? p.rows : 0,
    items: Array.isArray(p?.items) ? p.items : [],
  };
}

/**
 * The same defaulting for the vault, whose empty is a capacity of zero.
 *
 * A zero capacity is the honest "the server has not said yet": the panel reads it as
 * *waiting* rather than as a full vault, because 0 of 0 stored is not a limit anyone
 * has hit. Separate from `normaliseContainer` because the shapes are different — one
 * has a rectangle and the other a count — and a single function taking both would have
 * to guess which it was given.
 */
export function normaliseVault(p: Partial<VaultPayload> | undefined): VaultPayload {
  return {
    capacity: typeof p?.capacity === 'number' ? p.capacity : 0,
    items: Array.isArray(p?.items) ? p.items : [],
  };
}

/**
 * An item's placement, with the wire's absent-means-zero convention resolved.
 *
 * The one place that convention is read, so no component has to know it. `col: 0` and
 * `row: 0` are dropped by `omitempty` and they are the top-left square — the commonest
 * placement there is — so every consumer has to default them to zero rather than
 * treating absence as unknown.
 *
 * A **missing size** is different: it defaults to 1 × 1, because a zero-cell item
 * would overlap nothing and could be drawn on top of anything. The server never sends
 * one for a carried item, and if it ever did, a single cell is the reading that keeps
 * the grid coherent.
 */
export function placementOf(item: InventoryItem): {
  col: number;
  row: number;
  width: number;
  height: number;
} {
  return {
    col: item.col ?? 0,
    row: item.row ?? 0,
    width: item.width ?? 1,
    height: item.height ?? 1,
  };
}

function decodeTestMode(p: Partial<TestModeInfo>): TestModeInfo {
  return {
    commands: Array.isArray(p.commands) ? p.commands : [],
    bases: Array.isArray(p.bases) ? p.bases : [],
    // The server's cap. Defaulted to the shipped 100 so a level field is bounded by
    // something rather than by NaN.
    maxLevel: typeof p.maxLevel === 'number' && p.maxLevel > 0 ? p.maxLevel : 100,
  };
}

// ── Party, social and chat ──────────────────────────────────────────────────

/**
 * Where a message goes.
 *
 * A closed vocabulary mirrored from `game/chat.go`. `ZONE` is everybody in the same place —
 * the town, or one instance — and is the default because it is the only one that always has
 * somebody in it.
 */
export type ChatChannel = 'ZONE' | 'PARTY' | 'GUILD';

/** The channels, in the order the chat box draws its tabs. */
export const CHAT_CHANNELS: ChatChannel[] = ['ZONE', 'PARTY', 'GUILD'];

/** How long one message may be. Mirrored from `game.MaxChatLength`. */
export const MAX_CHAT_LENGTH = 200;

/** One card in the party HUD. */
export interface PartyMember {
  id: string;
  name: string;
  level: number;
  hp: number;
  maxHp: number;
  leader?: boolean;
}

/** The whole party. An absent `id` means no party. */
export interface PartyPayload {
  id?: string;
  leaderId?: string;
  members?: PartyMember[];
}

/** A party offer, as the invitee reads it. */
export interface PartyInviteView {
  fromName: string;
}

/**
 * The social panel.
 *
 * **Names only.** The panel exists so somebody can type a name they half-remember; ids are
 * handles the client has no use for and could not otherwise obtain.
 */
export interface SocialPayload {
  online?: string[];
  invite?: PartyInviteView;
}

export interface ChatMessage {
  channel: ChatChannel;
  fromId: string;
  fromName: string;
  text: string;
}

export interface ChatPayload {
  messages: ChatMessage[];
}
